// cSpell:ignore Filterand Pipelinev Filesv
import axios from 'axios';
import { TableServiceClient, TableClient } from "@azure/data-tables";
import { InteractiveBrowserCredential } from "@azure/identity";


const apiUrlBase = 'https://dev.azure.com/cyaniccloud';
const apiVersion = '6.0';

const BASELINE_PREREQUISITE_PATHS = [
  // Baseline paths
  '/M365Config/Resources/AzureAD/AAD_Groups/',
  '/M365Config/Resources/AzureAD/AAD_RequiredUsers/',
  '/M365Config/Resources/AzureAD/AAD_Required_Onboarding_Groups/',
  '/M365Config/Resources/AzureMaintenance/Configurations/',
  '/M365Config/Resources/Exchange/InboundConnectors/',
  '/M365Config/Resources/AzurePolicy/DefinitionPolicies/',

  // Client-specific paths (use wildcard for client code)
  '/Clients/[^/]+/M365Config/Resources/AzureAD/AAD_Groups/',
  '/Clients/[^/]+/M365Config/Resources/AzureAD/AAD_RequiredUsers/',
  '/Clients/[^/]+/M365Config/Resources/AzureAD/AAD_Required_Onboarding_Groups/',
  '/Clients/[^/]+/M365Config/Resources/AzureMaintenance/Configurations/',
  '/Clients/[^/]+/M365Config/Resources/Exchange/InboundConnectors/',
  '/Clients/[^/]+/M365Config/Resources/AzurePolicy/DefinitionPolicies/',

  // Add more patterns as needed in the future
];

const axiosInstance = axios.create({
  baseURL: apiUrlBase,
  headers: {
    'Content-Type': 'application/json',
  },
});

const isBaselinePrerequisite = (path) => {
  if (!path) return false;

  return BASELINE_PREREQUISITE_PATHS.some(pattern => {
    // Convert pattern to regex (supports wildcards)
    const regexPattern = pattern
      .replace(/\[/g, '\\[')
      .replace(/\]/g, '\\]')
      .replace(/\//g, '\\/')
      .replace(/\[\^\/\]\+/g, '[^/]+'); // Handle [^/]+ wildcard

    const regex = new RegExp(regexPattern, 'i');
    return regex.test(path);
  });
};


const setAuthHeader = (accessToken) => {
  axiosInstance.defaults.headers.common['Authorization'] = `Bearer ${accessToken}`;
};

const fetchProjects = async (accessToken) => {
  setAuthHeader(accessToken);
  try {
    const response = await axiosInstance.get(`_apis/projects?api-version=${apiVersion}`);
    return response.data.value;
  } catch (error) {
    console.error('Error fetching projects:', error);
    throw error;
  }
};

const fetchPipelines = async (accessToken) => {
  setAuthHeader(accessToken);
  try {
    const response = await axiosInstance.get(`/tenants/_apis/pipelines?api-version=7.1-preview.1`);
    return response.data.value;
  } catch (error) {
    console.error('Error fetching pipelines:', error);
    throw error;
  }
};

const fetchPipelinesWithFilter = async (accessToken) => {
  setAuthHeader(accessToken);
  try {
    const response = await axiosInstance.get(`/tenants/_apis/pipelines?api-version=7.1-preview.1`);
    return response.data.value.filter(pipeline => {
      const folder = pipeline.folder.replace('\\', '').trim();
      return folder.length === 3;
    });
  } catch (error) {
    console.error('Error fetching pipelines:', error);
    throw error;
  }
};

const fetchPipelinesWithFilterandProject = async (accessToken, projectName) => {
  setAuthHeader(accessToken);
  try {
    const response = await axiosInstance.get(`/${projectName}/_apis/pipelines?api-version=7.1-preview.1`);
    return response.data.value.filter(pipeline => {
      const folder = pipeline.folder.replace('\\', '').trim();
      // Include pipelines where folder length is 3 or folder contains "Deploy Baseline Tenant Configuration" or "Capture Client Configuration Pipeline"
      return folder.length === 3 || folder.includes('Deploy Baseline Tenant Configuration');
    });
  } catch (error) {
    console.error('Error fetching pipelines:', error);
    throw error;
  }
};


const fetchPipelinesWithFolderFilter = async (accessToken, projectName, folderFilter) => {
  setAuthHeader(accessToken);
  try {
    const response = await axiosInstance.get(`/${projectName}/_apis/pipelines?api-version=7.1-preview.1`);
    return response.data.value.filter(pipeline => {
      const folder = pipeline.folder.replace('\\', '').trim();
      // Include pipelines where folder length is 3 or folder contains the specified filter
      return folder.length === 3 || folder.includes(folderFilter);
    });
  } catch (error) {
    console.error('Error fetching pipelines:', error);
    throw error;
  }
};


const fetchPipelinesbyName = async (accessToken, projectName) => {
  setAuthHeader(accessToken);
  try {
    const response = await axiosInstance.get(`/${projectName}/_apis/pipelines?api-version=7.1-preview.1`);
    return response.data.value.filter(pipeline => {
      const folder = pipeline.folder.charAt(0) === '\\' ? pipeline.folder.slice(1) : pipeline.folder;
      return folder.length === 3;
    });
  } catch (error) {
    console.error('Error fetching pipelines:', error);
    throw error;
  }
};

const runPipeline = async (accessToken, projectId, pipelineId) => {
  setAuthHeader(accessToken);
  const apiUrl = `https://dev.azure.com/cyaniccloud/${projectId}/_apis/pipelines/${pipelineId}/runs?api-version=7.1-preview.1`;

  const body = {
    "stagesToSkip": [],
    "resources": {
      "repositories": {
        "self": {
          "refName": "refs/heads/main"
        }
      }
    },
    "variables": {}
  };

  try {
    await axiosInstance.post(apiUrl, body);
  } catch (error) {
    console.error('Error running pipeline:', error);
    throw error;
  }
};

const runFullBaselinePipeline = async (accessToken, projectId, pipelineId) => {
  setAuthHeader(accessToken);
  const apiUrl = `https://dev.azure.com/cyaniccloud/${projectId}/_apis/pipelines/${pipelineId}/runs?api-version=7.1-preview.1`;

  const body = {
    "stagesToSkip": [],
    "resources": {
      "repositories": {
        "self": {
          "refName": "refs/heads/main"
        }
      }
    },
    "variables": {}  // Assuming no variables are needed here
  };

  try {
    const response = await axiosInstance.post(apiUrl, body);

    // Return the run ID (as done in `triggerOnboardBuild`)
    return response.data.id;
  } catch (error) {
    console.error('Error running pipeline:', error);
    throw error;
  }
};

const runCustomBaselinePipeline = async (accessToken, projectId, pipelineId, componentsJson = null) => {
  setAuthHeader(accessToken);
  const apiUrl = `https://dev.azure.com/cyaniccloud/${projectId}/_apis/pipelines/${pipelineId}/runs?api-version=7.1-preview.1`;

  // Base body structure for pipeline run
  const body = {
    "stagesToSkip": [],
    "resources": {
      "repositories": {
        "self": {
          "refName": "refs/heads/main"
        }
      }
    },
    "templateParameters": {} // Use templateParameters for passing parameters
  };

  // If componentsJson is provided, include it in the templateParameters
  if (componentsJson) {
    body.templateParameters.Components = componentsJson; // Pass as a parameter
  }

  try {
    const response = await axiosInstance.post(apiUrl, body);

    // Return the run ID like in `runFullBaselinePipeline`
    return response.data.id;  // Ensure it returns the run ID
  } catch (error) {
    console.error('Error running pipeline:', error);
    throw error;
  }
};


const runOffboardPipeline = async (accessToken, projectId, pipelineId, componentsJson = null) => {
  setAuthHeader(accessToken);
  const apiUrl = `https://dev.azure.com/cyaniccloud/${projectId}/_apis/pipelines/${pipelineId}/runs?api-version=7.1-preview.1`;

  // Base body structure for pipeline run
  const body = {
    "stagesToSkip": [],
    "resources": {
      "repositories": {
        "self": {
          "refName": "refs/heads/main"
        }
      }
    },
    "templateParameters": {} // Use templateParameters for passing parameters
  };

  // If componentsJson is provided, include it in the templateParameters
  if (componentsJson) {
    body.templateParameters.Components = componentsJson; // Pass as a parameter
  }

  try {
    const response = await axiosInstance.post(apiUrl, body);

    // Return the run ID like in `runCustomBaselinePipeline`
    return response.data.id;  // Ensure it returns the run ID
  } catch (error) {
    console.error('Error running off-board pipeline:', error);
    throw error;
  }
};


const runComponentPipeline = async (accessToken, projectId, pipelineId, componentsJson = null) => {
  setAuthHeader(accessToken);
  const apiUrl = `https://dev.azure.com/cyaniccloud/${projectId}/_apis/pipelines/${pipelineId}/runs?api-version=7.1-preview.1`;

  // Base body structure for pipeline run
  const body = {
    "stagesToSkip": [],
    "resources": {
      "repositories": {
        "self": {
          "refName": "refs/heads/main"
        }
      }
    },
    "variables": {}
  };

  // If componentsJson is provided, include it in the variables
  if (componentsJson) {
    body.variables.Components = {
      value: componentsJson,
      isSecret: false
    };
  }

  try {
    await axiosInstance.post(apiUrl, body);
  } catch (error) {
    console.error('Error running pipeline:', error);
    throw error;
  }
};

const runComponentPipelinev2 = async (accessToken, projectId, pipelineId, componentsJson = null) => {
  setAuthHeader(accessToken);
  const apiUrl = `https://dev.azure.com/cyaniccloud/${projectId}/_apis/pipelines/${pipelineId}/runs?api-version=7.1-preview.1`;

  // Base body structure for pipeline run
  const body = {
    "stagesToSkip": [],
    "resources": {
      "repositories": {
        "self": {
          "refName": "refs/heads/main"
        }
      }
    },
    "templateParameters": {} // Use templateParameters for passing parameters
  };

  // If componentsJson is provided, include it in the templateParameters
  if (componentsJson) {
    body.templateParameters.Components = componentsJson; // Pass as a parameter
  }

  try {
    await axiosInstance.post(apiUrl, body);
  } catch (error) {
    console.error('Error running pipeline:', error);
    throw error;
  }
};


const triggerBuild = async (accessToken, projectId, pipelineBuildId, appName) => {
  setAuthHeader(accessToken);
  const apiUrl = `https://dev.azure.com/cyaniccloud/${projectId}/_apis/pipelines/${pipelineBuildId}/runs?api-version=7.1-preview.1`;
  const body = {
    "stagesToSkip": [],
    "resources": {
      "repositories": {
        "self": {
          "refName": "refs/heads/main"
        }
      }
    },
    "templateParameters": {
      "AppList": `- ${appName}`
    },
    "variables": {}
  };

  try {
    const response = await axiosInstance.post(apiUrl, body);
    return response.data;
  } catch (error) {
    console.error('Error triggering build:', error);
    throw error;
  }
};

const triggerAppDeployBuild_old = async (accessToken, projectId, pipelineBuildId, ClientCode, Domain, UserName, Password) => {
  setAuthHeader(accessToken);
  const apiUrl = `https://dev.azure.com/cyaniccloud/${projectId}/_apis/pipelines/${pipelineBuildId}/runs?api-version=7.1-preview.1`;
  const body = {
    "stagesToSkip": [],
    "resources": {
      "repositories": {
        "self": {
          "refName": "refs/heads/main"
        }
      }
    },
    "templateParameters": {
      "ClientCode": ClientCode,
      "Domain": Domain
    },
    "variables": {
      "TenantAdminUserName": {
        "isSecret": false,
        "value": UserName
      },
      "TenantAdminUserPW": {
        "isSecret": true,
        "value": Password
      }
    }
  };

  try {
    const response = await axiosInstance.post(apiUrl, body);
    return response.data;
  } catch (error) {
    console.error('Error triggering build:', error);
    throw error;
  }
};

const triggerAppDeployBuild = async (accessToken, projectId, pipelineBuildId, ClientCode, Domain, ClientTenantID, EnterpriseAppID) => {
  setAuthHeader(accessToken);
  const apiUrl = `https://dev.azure.com/cyaniccloud/${projectId}/_apis/pipelines/${pipelineBuildId}/runs?api-version=7.1-preview.1`;
  const body = {
    "stagesToSkip": [],
    "resources": {
      "repositories": {
        "self": {
          "refName": "refs/heads/main"
        }
      }
    },
    "templateParameters": {
      "ClientCode": ClientCode,
      "Domain": Domain,
      "ClientTenantID": ClientTenantID,
      "EnterpriseAppID": EnterpriseAppID
    }
  };

  try {
    const response = await axiosInstance.post(apiUrl, body);
    return response.data;
  } catch (error) {
    console.error('Error triggering build:', error);
    throw error;
  }
};


const fetchJsonFile = async (accessToken, repoId, path) => {
  setAuthHeader(accessToken);
  const apiUrl = `https://dev.azure.com/cyaniccloud/1b238677-4a8d-4243-827e-88a0558c085a/_apis/git/repositories/${repoId}/items?path=${encodeURIComponent(path)}&recursionLevel=0&includeContentMetadata=true&versionDescriptor.version=main&versionDescriptor.versionOptions=0&versionDescriptor.versionType=0&includeContent=true&resolveLfs=true`;

  try {
    const response = await axiosInstance.get(apiUrl);
    return response.data;
  } catch (error) {
    console.error(`Error fetching JSON file from ${path}:`, error);
    throw error;
  }
};

const fetchFileContent = async (accessToken, repoId, path, branch = 'main') => {
  const headers = {
    'Authorization': `Bearer ${accessToken}`
  };

  try {
    const response = await axios.get(
      `${apiUrlBase}/1b238677-4a8d-4243-827e-88a0558c085a/_apis/git/repositories/${repoId}/items`,
      {
        headers,
        params: {
          'path': path,
          'versionDescriptor.version': branch,
          'versionDescriptor.versionType': 'branch',
          'includeContent': true,
          'api-version': '7.0'
        }
      }
    );

    // Return decoded content directly
    if (response.data.content) {
      return decodeBase64UTF8(response.data.content);
    }

    throw new Error('No content returned from API');
  } catch (error) {
    console.error(`Error fetching file ${path} from branch ${branch}:`, error);
    throw error;
  }
};

const fetchDirectoryContents = async (accessToken, repoId, directoryPath) => {
  setAuthHeader(accessToken);

  // Clean up the directory path - ensure it starts with /
  const cleanPath = directoryPath.startsWith('/') ? directoryPath : '/' + directoryPath;

  // Use the same pattern as fetchClients and other functions in this file
  const apiUrl = `https://dev.azure.com/cyaniccloud/1b238677-4a8d-4243-827e-88a0558c085a/_apis/git/repositories/${repoId}/items?scopePath=${encodeURIComponent(cleanPath)}&recursionLevel=OneLevel&api-version=6.0`;

  try {
    const response = await axiosInstance.get(apiUrl);
    const allItems = response.data.value || [];

    // Filter out the directory itself (gitObjectType: "tree") and only return files (gitObjectType: "blob")
    const filesOnly = allItems.filter(item => item.gitObjectType === 'blob');

    return filesOnly;
  } catch (error) {
    console.error(`Error fetching directory contents from ${directoryPath}:`, error);
    throw error;
  }
};

const fetchYamlFile = async (accessToken, repoId, path) => {
  setAuthHeader(accessToken);

  // Correct project ID in the URL
  const projectId = '65a332c6-445e-4c72-a097-db3513d145d5';

  // Correct API endpoint
  const apiUrl = `https://dev.azure.com/cyaniccloud/${projectId}/_apis/git/repositories/${repoId}/items?path=${encodeURIComponent(path)}&recursionLevel=0&includeContentMetadata=true&versionDescriptor.version=main&versionDescriptor.versionOptions=0&versionDescriptor.versionType=0&includeContent=true&resolveLfs=true`;

  try {
    const response = await axiosInstance.get(apiUrl);
    return response.data;
  } catch (error) {
    console.error(`Error fetching YAML file from ${path}:`, error);
    throw error;
  }
};


const fetchClients = async (accessToken, repoId, path) => {
  setAuthHeader(accessToken);
  const apiUrl = `https://dev.azure.com/cyaniccloud/1b238677-4a8d-4243-827e-88a0558c085a/_apis/git/repositories/${repoId}/items?scopePath=${encodeURIComponent(path)}&recursionLevel=OneLevel&api-version=6.0`;

  try {
    const response = await axiosInstance.get(apiUrl);
    const directories = response.data.value.filter(item => item.isFolder);
    return directories.map(dir => dir.path.split('/').pop());
  } catch (error) {
    console.error('Error fetching clients:', error);
    throw error;
  }
};


const compareJsonObjects = (referenceObject, differenceObject, basePath = '', excludedProperties = []) => {
  let comparisonResults = [];

  for (let property in referenceObject) {
    const fullPropertyPath = basePath ? `${basePath}.${property}` : property;

    if (excludedProperties.includes(fullPropertyPath)) {
      continue;
    }

    const refValue = referenceObject[property];
    const diffValue = differenceObject[property];

    if (diffValue === undefined) {
      comparisonResults.push({
        Path: fullPropertyPath,
        RefValue: refValue,
        DiffValue: 'Missing',
        Equal: false
      });
      continue;
    }

    if (Array.isArray(refValue) && Array.isArray(diffValue)) {
      const arraysEqual = JSON.stringify(refValue) === JSON.stringify(diffValue);
      comparisonResults.push({
        Path: fullPropertyPath,
        RefValue: JSON.stringify(refValue),
        DiffValue: JSON.stringify(diffValue),
        Equal: arraysEqual
      });
    } else if (typeof refValue === 'object' && refValue !== null && typeof diffValue === 'object' && diffValue !== null) {
      const subResults = compareJsonObjects(refValue, diffValue, fullPropertyPath, excludedProperties);
      comparisonResults = comparisonResults.concat(subResults);
    } else {
      const equal = refValue === diffValue;
      comparisonResults.push({
        Path: fullPropertyPath,
        RefValue: refValue,
        DiffValue: diffValue,
        Equal: equal
      });
    }
  }

  for (let property in differenceObject) {
    const fullPropertyPath = basePath ? `${basePath}.${property}` : property;
    if (!Object.prototype.hasOwnProperty.call(referenceObject, property) && !excludedProperties.includes(fullPropertyPath)) {
      comparisonResults.push({
        Path: fullPropertyPath,
        RefValue: 'Missing',
        DiffValue: differenceObject[property],
        Equal: false
      });
    }
  }

  return comparisonResults;
};

const compareConfigurations = async (accessToken, baselineUrl, clientUrl) => {
  // Set headers for authentication
  const headers = {
    'Authorization': `Bearer ${accessToken}`,
    'Content-Type': 'application/json'
  };

  try {
    // Fetch baseline and client data
    const [baselineResponse, clientResponse] = await Promise.all([
      axios.get(baselineUrl, { headers }),
      axios.get(clientUrl, { headers })
    ]);

    // Parse content
    const baselineContent = JSON.parse(baselineResponse.data.content);
    const clientContent = JSON.parse(clientResponse.data.content);

    // Compare and group by displayName (or any other key of interest)
    let comparisonResults = {};
    if (baselineContent.displayName === clientContent.displayName) {
      const group = baselineContent.displayName;
      comparisonResults[group] = comparisonResults[group] || [];

      // Here, add a comparison logic for each property in JSON
      Object.keys(baselineContent).forEach((key) => {
        const baselineValue = baselineContent[key];
        const clientValue = clientContent[key];

        comparisonResults[group].push({
          Path: key,
          RefValue: baselineValue,
          DiffValue: clientValue,
          Equal: baselineValue === clientValue
        });
      });
    }

    return comparisonResults;

  } catch (error) {
    console.error('Error comparing configurations:', error);
    throw error;
  }
};


const fetchAndCompareFiles = async (accessToken, repo, baselinePath, clientPath, excludedProperties) => {
  try {
    const baselineFile = await fetchJsonFile(accessToken, repo, baselinePath);
    const clientFile = await fetchJsonFile(accessToken, repo, clientPath);

    const baselineContent = JSON.parse(baselineFile.content);
    const clientContent = JSON.parse(clientFile.content);

    const comparisonResults = compareJsonObjects(baselineContent, clientContent, '', excludedProperties);
    console.log(comparisonResults);

    return comparisonResults;
  } catch (error) {
    console.error('Error comparing files:', error);
    throw error;
  }
};

const fetchAllJsonFiles = async (accessToken, repoId, path) => {
  const apiUrl = `https://dev.azure.com/cyaniccloud/1b238677-4a8d-4243-827e-88a0558c085a/_apis/git/repositories/${repoId}/items?scopePath=${encodeURIComponent(path)}&recursionLevel=Full&api-version=6.0`;
  const headers = {
    'Authorization': `Bearer ${accessToken}`,
  };

  try {
    const response = await axios.get(apiUrl, { headers });
    const files = response.data.value.filter(item => !item.isFolder && item.path.endsWith('.json'));
    return files.map(file => file.path);
  } catch (error) {
    console.error(`Error fetching JSON files from ${path}:`, error);
    throw error;
  }
};

const fetchAndCompareAllFilesv6 = async (
  accessToken,
  repoId,
  baselineDir,
  clientDir,
  excludedProperties,
  progressCallback,
  excludedPaths = []
) => {
  try {
    // Fetch baseline and client files, excluding specified folders
    const baselineFiles = await fetchAllJsonFilesv3(accessToken, repoId, baselineDir, excludedPaths);
    const clientFiles = await fetchAllJsonFilesv3(accessToken, repoId, clientDir, excludedPaths);

    let groupedComparisonResults = {};
    const totalFiles = baselineFiles.length;

    // Retrieve grouped items for better organization
    const groupedItems = await getAllItemsGroupedByTopLevelArea(accessToken);

    for (let i = 0; i < totalFiles; i++) {
      const baselineFile = baselineFiles[i];
      const relativePath = baselineFile.replace(baselineDir, '');
      const clientFilePath = clientFiles.find(clientFile => clientFile.endsWith(relativePath));

      let results = [];

      if (clientFilePath) {
        const baselineFileContent = await fetchJsonFile(accessToken, repoId, baselineFile);
        const clientFileContent = await fetchJsonFile(accessToken, repoId, clientFilePath);

        const baselineContent = JSON.parse(baselineFileContent.content);
        const clientContent = JSON.parse(clientFileContent.content);

        results = compareJsonObjects(baselineContent, clientContent, '', excludedProperties);
      } else {
        results.push({
          Path: relativePath,
          RefValue: JSON.parse((await fetchJsonFile(accessToken, repoId, baselineFile)).content),
          DiffValue: 'Missing',
          Equal: false,
        });
      }

      // Determine the category (area) for the configuration file
      const configName = relativePath.split('/').pop().replace('.json', '');
      const area = Object.keys(groupedItems).find(area =>
        groupedItems[area].some(item => item.path.endsWith(relativePath))
      );

      // Group results by area and config name
      if (!groupedComparisonResults[area]) {
        groupedComparisonResults[area] = {};
      }
      groupedComparisonResults[area][configName] = results;

      // Update progress
      const progress = Math.floor(((i + 1) / totalFiles) * 100);
      if (progressCallback) {
        progressCallback(progress);
      }
    }

    return groupedComparisonResults;
  } catch (error) {
    console.error('Error comparing files:', error);
    throw error;
  }
};


const fetchAllJsonFilesv3 = async (accessToken, repoId, path, excludedPaths = []) => {
  const apiUrl = `https://dev.azure.com/cyaniccloud/1b238677-4a8d-4243-827e-88a0558c085a/_apis/git/repositories/${repoId}/items?scopePath=${encodeURIComponent(path)}&recursionLevel=Full&api-version=6.0`;
  const headers = { Authorization: `Bearer ${accessToken}` };

  try {
    const response = await axios.get(apiUrl, { headers });

    if (!response.data.value || !Array.isArray(response.data.value)) {
      throw new Error("Unexpected response format from Azure DevOps API");
    }

    //  Normalize excluded paths (force leading /, remove trailing slashes, lowercase)
    const normalizedExcludedPaths = excludedPaths.map(p =>
      (p.startsWith("/") ? p : "/" + p).replace(/\/+$/, "").toLowerCase()
    );

    console.log(" Normalized Excluded Paths Before Filtering:", normalizedExcludedPaths);

    //  Log all file paths from API response
    console.log(" All Paths from API:", response.data.value.map(item => item.path));

    //  Filter out directories and unwanted files
    const files = response.data.value.filter(item => {
      const isJsonFile = !item.isFolder && item.path.toLowerCase().endsWith('.json');
      const normalizedPath = item.path.toLowerCase();

      //  Check if it's excluded
      const isExcluded = normalizedExcludedPaths.some(excludedPath => {
        const matches = normalizedPath.startsWith(excludedPath);
        if (matches) {
          console.log(` Excluding: ${item.path} (Matched: ${excludedPath})`);
        }
        return matches;
      });

      return isJsonFile && !isExcluded;
    });

    console.log(" Filtered files from", path, files.map(f => f.path)); // Debugging output
    return files.map(file => file.path);
  } catch (error) {
    console.error(`Error fetching JSON files from ${path}:`, error.response ? error.response.data : error.message);
    throw error;
  }
};






const fetchAllJsonFilesv2 = async (accessToken, repoId, path, progressCallback) => {
  const apiUrl = `https://dev.azure.com/cyaniccloud/1b238677-4a8d-4243-827e-88a0558c085a/_apis/git/repositories/${repoId}/items?scopePath=${encodeURIComponent(path)}&recursionLevel=Full&api-version=6.0`;
  const headers = {
    'Authorization': `Bearer ${accessToken}`,
  };

  try {
    const response = await axios.get(apiUrl, { headers });
    const files = response.data.value.filter(item => !item.isFolder && item.path.endsWith('.json'));

    // Initialize progress tracking
    const totalFiles = files.length;
    let processedFiles = 0;

    // Report progress after each file is processed
    for (let i = 0; i < totalFiles; i++) {
      processedFiles += 1;
      if (progressCallback) {
        const progress = Math.floor((processedFiles / totalFiles) * 100);
        progressCallback(progress);
      }
    }

    return files.map(file => file.path);
  } catch (error) {
    console.error(`Error fetching JSON files from ${path}:`, error);
    throw error;
  }
};


const fetchAndCompareAllFiles = async (accessToken, repoId, baselineDir, clientDir, excludedProperties) => {
  try {
    const baselineFiles = await fetchAllJsonFiles(accessToken, repoId, baselineDir);
    const clientFiles = await fetchAllJsonFiles(accessToken, repoId, clientDir);

    let comparisonResults = [];

    for (let baselineFile of baselineFiles) {
      const relativePath = baselineFile.replace(baselineDir, '');
      const clientFilePath = clientFiles.find(clientFile => clientFile.endsWith(relativePath));

      if (clientFilePath) {
        const baselineFileContent = await fetchJsonFile(accessToken, repoId, baselineFile);
        const clientFileContent = await fetchJsonFile(accessToken, repoId, clientFilePath);

        const baselineContent = JSON.parse(baselineFileContent.content);
        const clientContent = JSON.parse(clientFileContent.content);

        const results = compareJsonObjects(baselineContent, clientContent, '', excludedProperties);
        comparisonResults = comparisonResults.concat(results);
      } else {
        comparisonResults.push({
          Path: relativePath,
          RefValue: JSON.parse((await fetchJsonFile(accessToken, repoId, baselineFile)).content),
          DiffValue: 'Missing',
          Equal: false
        });
      }
    }

    return comparisonResults;
  } catch (error) {
    console.error('Error comparing files:', error);
    throw error;
  }
};

const fetchAndCompareAllFilesv2 = async (accessToken, repoId, baselineDir, clientDir, excludedProperties) => {
  try {
    const baselineFiles = await fetchAllJsonFiles(accessToken, repoId, baselineDir);
    const clientFiles = await fetchAllJsonFiles(accessToken, repoId, clientDir);

    let groupedComparisonResults = {}; // Ensure this is initialized correctly

    for (let baselineFile of baselineFiles) {
      const relativePath = baselineFile.replace(baselineDir, '');
      const clientFilePath = clientFiles.find(clientFile => clientFile.endsWith(relativePath));

      let results = [];
      if (clientFilePath) {
        const baselineFileContent = await fetchJsonFile(accessToken, repoId, baselineFile);
        const clientFileContent = await fetchJsonFile(accessToken, repoId, clientFilePath);

        const baselineContent = JSON.parse(baselineFileContent.content);
        const clientContent = JSON.parse(clientFileContent.content);

        results = compareJsonObjects(baselineContent, clientContent, '', excludedProperties);
      } else {
        results.push({
          Path: relativePath,
          RefValue: JSON.parse((await fetchJsonFile(accessToken, repoId, baselineFile)).content),
          DiffValue: 'Missing',
          Equal: false
        });
      }

      // Ensure that results for each file are stored in groupedComparisonResults
      groupedComparisonResults[relativePath] = results; // Organize by file/policy
    }

    return groupedComparisonResults; // Return results grouped by file/policy
  } catch (error) {
    console.error('Error comparing files:', error);
    throw error;
  }
};

const fetchAndCompareAllFilesv3 = async (accessToken, repoId, baselineDir, clientDir, excludedProperties) => {
  try {
    const baselineFiles = await fetchAllJsonFiles(accessToken, repoId, baselineDir);
    const clientFiles = await fetchAllJsonFiles(accessToken, repoId, clientDir);

    let groupedComparisonResults = {}; // Object to store comparison results grouped by policy

    for (let baselineFile of baselineFiles) {
      const relativePath = baselineFile.replace(baselineDir, '');
      const clientFilePath = clientFiles.find(clientFile => clientFile.endsWith(relativePath));

      let results = [];
      if (clientFilePath) {
        const baselineFileContent = await fetchJsonFile(accessToken, repoId, baselineFile);
        const clientFileContent = await fetchJsonFile(accessToken, repoId, clientFilePath);

        const baselineContent = JSON.parse(baselineFileContent.content);
        const clientContent = JSON.parse(clientFileContent.content);

        results = compareJsonObjects(baselineContent, clientContent, '', excludedProperties);
      } else {
        results.push({
          Path: relativePath,
          RefValue: JSON.parse((await fetchJsonFile(accessToken, repoId, baselineFile)).content),
          DiffValue: 'Missing',
          Equal: false
        });
      }

      // Organize results by the configuration name (e.g., displayName or policy name)
      const configName = relativePath.split('/').pop().replace('.json', '');
      groupedComparisonResults[configName] = results;
    }

    return groupedComparisonResults; // Return results grouped by policy
  } catch (error) {
    console.error('Error comparing files:', error);
    throw error;
  }
};

const fetchAndCompareAllFilesv4 = async (accessToken, repoId, baselineDir, clientDir, excludedProperties, progressCallback) => {
  try {
    const baselineFiles = await fetchAllJsonFiles(accessToken, repoId, baselineDir);
    const clientFiles = await fetchAllJsonFiles(accessToken, repoId, clientDir);

    let groupedComparisonResults = {};
    const totalFiles = baselineFiles.length; // Total files for progress calculation

    for (let i = 0; i < totalFiles; i++) {
      const baselineFile = baselineFiles[i];
      const relativePath = baselineFile.replace(baselineDir, '');
      const clientFilePath = clientFiles.find(clientFile => clientFile.endsWith(relativePath));

      let results = [];
      if (clientFilePath) {
        const baselineFileContent = await fetchJsonFile(accessToken, repoId, baselineFile);
        const clientFileContent = await fetchJsonFile(accessToken, repoId, clientFilePath);

        const baselineContent = JSON.parse(baselineFileContent.content);
        const clientContent = JSON.parse(clientFileContent.content);

        results = compareJsonObjects(baselineContent, clientContent, '', excludedProperties);
      } else {
        results.push({
          Path: relativePath,
          RefValue: JSON.parse((await fetchJsonFile(accessToken, repoId, baselineFile)).content),
          DiffValue: 'Missing',
          Equal: false
        });
      }

      const configName = relativePath.split('/').pop().replace('.json', '');
      groupedComparisonResults[configName] = results;

      // Calculate and update progress
      const progress = Math.floor(((i + 1) / totalFiles) * 100);
      if (progressCallback) {
        progressCallback(progress); // Call the callback with the progress
      }
    }

    return groupedComparisonResults;
  } catch (error) {
    console.error('Error comparing files:', error);
    throw error;
  }
};
const fetchAndCompareAllFilesv5 = async (accessToken, repoId, baselineDir, clientDir, excludedProperties, progressCallback) => {
  try {
    const baselineFiles = await fetchAllJsonFiles(accessToken, repoId, baselineDir);
    const clientFiles = await fetchAllJsonFiles(accessToken, repoId, clientDir);

    let groupedComparisonResults = {};
    const totalFiles = baselineFiles.length;

    // Get the grouped items structure to use for area categorization
    const groupedItems = await getAllItemsGroupedByTopLevelArea(accessToken);

    for (let i = 0; i < totalFiles; i++) {
      const baselineFile = baselineFiles[i];
      const relativePath = baselineFile.replace(baselineDir, '');
      const clientFilePath = clientFiles.find(clientFile => clientFile.endsWith(relativePath));

      let results = [];
      if (clientFilePath) {
        const baselineFileContent = await fetchJsonFile(accessToken, repoId, baselineFile);
        const clientFileContent = await fetchJsonFile(accessToken, repoId, clientFilePath);

        const baselineContent = JSON.parse(baselineFileContent.content);
        const clientContent = JSON.parse(clientFileContent.content);

        results = compareJsonObjects(baselineContent, clientContent, '', excludedProperties);
      } else {
        results.push({
          Path: relativePath,
          RefValue: JSON.parse((await fetchJsonFile(accessToken, repoId, baselineFile)).content),
          DiffValue: 'Missing',
          Equal: false
        });
      }

      // Determine the config name and area from groupedItems
      const configName = relativePath.split('/').pop().replace('.json', '');
      const area = Object.keys(groupedItems).find(area =>
        groupedItems[area].some(item => item.path.endsWith(relativePath))
      );

      // Include area as part of grouped comparison results
      if (!groupedComparisonResults[area]) {
        groupedComparisonResults[area] = {};
      }
      groupedComparisonResults[area][configName] = results;

      // Calculate and update progress
      const progress = Math.floor(((i + 1) / totalFiles) * 100);
      if (progressCallback) {
        progressCallback(progress);
      }
    }

    return groupedComparisonResults;
  } catch (error) {
    console.error('Error comparing files:', error);
    throw error;
  }
};




// Define your Azure Storage account name and key here
const accountName = 'cccadostrgwe';

// Function to create a TableServiceClient
async function createTableServiceClient() {
  const credential = new InteractiveBrowserCredential({
    clientId: '1da955b1-4ce4-481a-a857-0423ac697927', // replace with your client ID
    tenantId: 'bcc93690-805b-4911-b8c1-6e447795ef8b', // replace with your tenant ID
  });
  return new TableServiceClient(
    `https://${accountName}.table.core.windows.net`,
    credential
  );
}

const listAzureTables = async () => {
  const tableServiceClient = await createTableServiceClient();
  const tables = tableServiceClient.listTables();
  const result = [];
  for await (const table of tables) {
    result.push(table);
  }
  return result;
};

// Function to get table contents
const getTableContents = async (tableName) => {
  const credential = new InteractiveBrowserCredential({
    clientId: '1da955b1-4ce4-481a-a857-0423ac697927', // replace with your client ID
    tenantId: 'bcc93690-805b-4911-b8c1-6e447795ef8b', // replace with your tenant ID
  });
  const tableClient = new TableClient(
    `https://${accountName}.table.core.windows.net`,
    tableName,
    credential
  );
  const entitiesIter = tableClient.listEntities();
  const entities = [];
  for await (const entity of entitiesIter) {
    entities.push(entity);
  }
  return entities;
};

// Function to create a client table

const createClientTable = async (tableName) => {
  const tableServiceClient = await createTableServiceClient();
  let status = 201;
  let message = `Table '${tableName}' created successfully.`;

  try {
    await tableServiceClient.createTable(tableName, {
      onResponse: (response) => {
        if (response.status === 409) {
          status = 409;
          message = `Table '${tableName}' already exists.`;
        }
      }
    });
  } catch (error) {
    status = error.statusCode || 500;
    message = error.message || 'An unexpected error occurred';
  }

  return { status, message };
};
// Function to update a client table
const updateClientTable = async (tableName, entity) => {
  const tableServiceClient = await createTableServiceClient();
  try {

    const tableClient = new TableClient(
      `https://${accountName}.table.core.windows.net`,
      tableName,
      tableServiceClient
    );

    await tableClient.createEntity(entity);
    return { status: 200, message: `Table '${tableName}' updated successfully.` };
  } catch (error) {
    return { status: error.statusCode || 500, message: error.message || 'An unexpected error occurred' };
  }
};

const updateEntity = async (tableName, entity) => {
  const tableServiceClient = await createTableServiceClient();
  try {
    const tableClient = new TableClient(
      `https://${accountName}.table.core.windows.net`,
      tableName,
      tableServiceClient
    );

    await tableClient.updateEntity(entity, "Replace");
    return { status: 200, message: `Entity in table '${tableName}' updated successfully.` };
  } catch (error) {
    return { status: error.statusCode || 500, message: error.message || 'An unexpected error occurred' };
  }
};

const deleteClientTable = async (tableName, partitionKey, rowKey) => {
  const credential = await createTableServiceClient();
  try {
    const tableClient = new TableClient(
      `https://${accountName}.table.core.windows.net`,
      tableName,
      credential
    );

    await tableClient.deleteEntity(partitionKey, rowKey);
    return { status: 200, message: `Entity deleted successfully.` };
  } catch (error) {
    console.error('Error deleting entity:', error);
    return { status: error.statusCode || 500, message: error.message || 'An unexpected error occurred' };
  }
};

const getPipelineStatus = async (accessToken, projectId, pipelineId, runId) => {
  setAuthHeader(accessToken);
  const apiUrl = `https://dev.azure.com/cyaniccloud/${projectId}/_apis/pipelines/${pipelineId}/runs/${runId}?api-version=7.1-preview.1`;

  try {
    const response = await axiosInstance.get(apiUrl);
    return response.data;
  } catch (error) {
    console.error('Error fetching pipeline status:', error);
    throw error;
  }
};


const triggerOnboardBuild = async (accessToken, projectId, pipelineBuildId, ClientCode, Domain, UserName, Password, subscriptionForLogin) => {
  setAuthHeader(accessToken);
  const apiUrl = `https://dev.azure.com/cyaniccloud/${projectId}/_apis/pipelines/${pipelineBuildId}/runs?api-version=7.1-preview.1`;
  const body = {
    "stagesToSkip": [],
    "resources": {
      "repositories": {
        "self": {
          "refName": "refs/heads/main"
        }
      }
    },
    "templateParameters": {
      "ClientCode": ClientCode,
      "Domain": Domain,
      "subscriptionForLogin": subscriptionForLogin
    },
    "variables": {
      "TenantAdminUserName": {
        "isSecret": false,
        "value": UserName
      },
      "TenantAdminUserPW": {
        "isSecret": true,
        "value": Password
      }
    }
  };

  try {
    const response = await axiosInstance.post(apiUrl, body);
    return response.data.id; // Return the run ID of the triggered build
  } catch (error) {
    console.error('Error triggering build:', error);
    throw error;
  }
};

const triggerNewOnboardBuild = async (
  accessToken,
  projectId,
  pipelineBuildId,
  ClientCode,
  Domain,
  subscriptionForLogin,
  tenantId,
  enrollInPowerSupport,
  region
) => {
  setAuthHeader(accessToken);
  const apiUrl = `https://dev.azure.com/cyaniccloud/${projectId}/_apis/pipelines/${pipelineBuildId}/runs?api-version=7.1-preview.1`;

  const body = {
    "stagesToSkip": [],
    "resources": {
      "repositories": {
        "self": {
          "refName": "refs/heads/main"
        }
      }
    },
    "templateParameters": {
      "ClientCode": ClientCode,
      "Domain": Domain,
      "subscriptionForLogin": subscriptionForLogin,
      "TenantId": tenantId,
      "EnrollInPowerSupport": enrollInPowerSupport,
      "Region": region
    }

  };

  try {
    const response = await axiosInstance.post(apiUrl, body);
    return response.data.id;
  } catch (error) {
    console.error('Error triggering build:', error);
    throw error;
  }
};

const updateYamlFileContent = async (accessToken, repoId, path, content) => {
  setAuthHeader(accessToken);

  // Step 1: Get the latest commit ID
  const repoUrl = `https://dev.azure.com/cyaniccloud/_apis/git/repositories/${repoId}`;
  const refsUrl = `${repoUrl}/refs?filter=heads/main&api-version=6.0`;

  try {
    // Get the latest commit ID
    const refsResponse = await axiosInstance.get(refsUrl);
    const commitId = refsResponse.data.value[0].objectId;

    // Step 2: Create a new commit with the updated content
    const updateUrl = `${repoUrl}/pushes?api-version=6.0`;
    const body = {
      "refUpdates": [
        {
          "name": "refs/heads/main",
          "oldObjectId": commitId
        }
      ],
      "commits": [
        {
          "comment": "Updating YAML file with client application list",
          "changes": [
            {
              "changeType": "edit",
              "item": {
                "path": path
              },
              "newContent": {
                "content": content,
                "contentType": "rawtext"
              }
            }
          ]
        }
      ]
    };

    // Make the request to update the YAML file
    await axiosInstance.post(updateUrl, body);
    console.log("YAML file updated successfully.");
  } catch (error) {
    console.error("Error updating YAML file:", error);
    throw error;
  }
};






const getRepositories = async (accessToken) => {
  setAuthHeader(accessToken);
  const apiUrl = "https://dev.azure.com/cyaniccloud/1b238677-4a8d-4243-827e-88a0558c085a/_apis/git/repositories?api-version=6.0";

  try {
    const response = await axiosInstance.get(apiUrl);
    return response.data.value;
  } catch (error) {
    console.error("Error fetching repositories:", error);
    throw error;
  }
};

const getFolderStructureAndFiles = async (accessToken, repoId, path, progressCallback) => {
  setAuthHeader(accessToken);
  const apiUrl = `https://dev.azure.com/cyaniccloud/1b238677-4a8d-4243-827e-88a0558c085a/_apis/git/repositories/${repoId}/items?scopePath=${encodeURIComponent(
    path
  )}&recursionLevel=Full&api-version=6.0`;

  try {
    const response = await axiosInstance.get(apiUrl);
    const items = response.data.value;

    // Initialize progress tracking
    const totalItems = items.length;
    let processedItems = 0;

    // Report progress after each item is processed
    for (let i = 0; i < totalItems; i++) {
      processedItems += 1;
      if (progressCallback) {
        const progress = Math.floor((processedItems / totalItems) * 100);
        progressCallback(progress);
      }
    }

    return items;
  } catch (error) {
    console.error(`Error fetching items from ${path}:`, error);
    throw error;
  }
};


const getAllItemsGroupedByTopLevelArea = async (accessToken, progressCallback) => {
  try {
    const repositories = await getRepositories(accessToken);
    const allItems = [];

    for (const repo of repositories) {
      const items = await getFolderStructureAndFiles(accessToken, repo.id, "/M365Config/Resources", progressCallback);

      items.forEach((item) => {
        if (item.path) {
          const match = item.path.match("^/M365Config/Resources/([^/]+)");
          const topLevelArea = item.path === "/M365Config/Resources" ? "OverallBaseline" : match ? match[1] : "Uncategorized";

          allItems.push({
            repository: repo.name,
            path: item.path,
            type: item.gitObjectType,
            area: topLevelArea, // New property for grouping
          });
        }
      });
    }

    // Group items by top-level area
    const groupedItems = allItems.reduce((acc, item) => {
      if (!acc[item.area]) {
        acc[item.area] = [];
      }
      acc[item.area].push(item);
      return acc;
    }, {});

    return groupedItems;
  } catch (error) {
    console.error("Error processing items:", error);
  }
};


const getAllPipelineDefinitions = async (organization, projectName, accessToken) => {
  const headers = {
    Authorization: `Bearer ${accessToken}`,
    'Content-Type': 'application/json'
  };
  const pipelinesUrl = `https://dev.azure.com/${organization}/${projectName}/_apis/build/definitions?api-version=7.1-preview.7`;

  try {
    const response = await fetch(pipelinesUrl, { headers });
    const data = await response.json();
    return data.value;
  } catch (error) {
    console.error('Error fetching pipeline definitions:', error.message);
    throw error;
  }
};

const getPipelineDefinitionInfo = async (pipelineName, folder, organization, projectName, accessToken) => {
  const headers = {
    Authorization: `Bearer ${accessToken}`,
    'Content-Type': 'application/json'
  };
  const pipelinesUrl = `https://dev.azure.com/${organization}/${projectName}/_apis/build/definitions?api-version=7.1-preview.7`;

  try {
    const response = await fetch(pipelinesUrl, { headers });
    const data = await response.json();

    const pipeline = data.value.find(
      (item) => item.name === pipelineName && item.path === folder
    );

    if (!pipeline) {
      console.log(`No pipeline found for ${pipelineName} in ${folder}`);
      return null;
    }

    return {
      definitionId: pipeline.id,
      projectId: pipeline.project.id,
    };
  } catch (error) {
    console.error('Error fetching pipeline definition info:', error.message);
    throw error;
  }
};

const getExistingSchedules = async (organization, projectId, projectName, definitionId, folder, accessToken) => {
  const headers = {
    Authorization: `Bearer ${accessToken}`,
    'Content-Type': 'application/json',
  };

  try {
    // Get build definition
    const definitionUrl = `https://dev.azure.com/${organization}/${projectId}/_apis/build/definitions/${definitionId}?api-version=7.0&expand=properties`;
    const definitionResponse = await fetch(definitionUrl, { headers });

    if (!definitionResponse.ok) {
      throw new Error(`Failed to fetch build definition: ${definitionResponse.statusText}`);
    }

    const buildDefinition = await definitionResponse.json();

    // Get YAML content
    const yamlPath = buildDefinition.process.yamlFilename;
    const repoId = buildDefinition.repository.id;
    const yamlUrl = `https://dev.azure.com/${organization}/${projectName}/_apis/git/repositories/${repoId}/items`;

    const yamlHeaders = {
      ...headers,
      Accept: 'text/plain',
    };

    const yamlResponse = await fetch(`${yamlUrl}?path=${encodeURIComponent(yamlPath)}&api-version=7.0`, { headers: yamlHeaders });

    if (!yamlResponse.ok) {
      const errorText = await yamlResponse.text();
      throw new Error(`YAML fetch error: ${yamlResponse.status} - ${errorText}`);
    }

    const yamlString = await yamlResponse.text();
    //console.log("Fetched YAML Content (raw):", yamlString);

    // Check for schedules section
    const schedulePattern = /^\s*schedules:\s*(-\s*cron:.*?(?=\n\S))/ms;
    const scheduleMatch = yamlString.match(schedulePattern);

    if (!scheduleMatch) {
      console.log(`No schedules found in YAML for definition ID ${definitionId} in folder ${folder}`);
      return null;
    }

    const schedulesBlock = scheduleMatch[1];

    // Parse individual schedules
    const cronPattern = /-\s*cron:\s*['"]([^'"]+)['"]\s*displayName:\s*([^\n]+)\s*branches:\s*.*?include:\s*-\s*([^\n]+)\s*always:\s*(\w+)/g;
    const schedules = [];
    let match;

    while ((match = cronPattern.exec(schedulesBlock)) !== null) {
      schedules.push({
        Cron: match[1],
        DisplayName: match[2].trim(),
        Branches: match[3].trim(),
        Always: match[4] === 'true',
      });
    }

    if (schedules.length === 0) {
      console.log(`No matching schedules found in YAML block for definition ID ${definitionId} in folder ${folder}`);
      return null;
    }

    return schedules;

  } catch (error) {
    console.error(`Failed to retrieve schedules for definition ID ${definitionId} in folder ${folder}`);
    console.error('Error:', error);
    return null;
  }
};

const UpdateClientSchedule = async (
  organization,
  projectId,
  projectName,
  definitionId,
  scheduleDetails,
  accessToken
) => {
  const headers = {
    Authorization: `Bearer ${accessToken}`,
    'Content-Type': 'application/json',
  };

  try {
    // Step 1: Fetch the current pipeline definition
    const definitionUrl = `https://dev.azure.com/${organization}/${projectId}/_apis/build/definitions/${definitionId}?api-version=7.0`;
    const definitionResponse = await fetch(definitionUrl, { headers });
    const definition = await definitionResponse.json();

    // Step 2: Get the current YAML content
    const yamlPath = definition.process.yamlFilename;
    const repoId = definition.repository.id;
    const yamlUrl = `https://dev.azure.com/${organization}/${projectName}/_apis/git/repositories/${repoId}/items`;

    const yamlResponse = await fetch(`${yamlUrl}?path=${yamlPath}&api-version=7.0`, {
      headers: {
        ...headers,
        Accept: 'text/plain',
      },
    });

    if (!yamlResponse.ok) {
      throw new Error('Failed to fetch YAML content');
    }

    let yamlContent = await yamlResponse.text();

    // Step 3: Create a new schedule block
    const newSchedule = `
  - cron: '${scheduleDetails.cronExpression}'
    displayName: ${scheduleDetails.displayName}
    branches:
      include:
        - main
    always: true`;

    // Step 4: Replace or add the schedules block
    if (yamlContent.includes('schedules:')) {
      // Replace the entire schedules block with the new schedule
      yamlContent = yamlContent.replace(
        /schedules:\s*\n\s*-\s*cron:.*?(?=\n\S|$)/s,
        `schedules:${newSchedule}\n\n`
      );
    } else {
      // Insert schedules block after "trigger: none"
      yamlContent = yamlContent.replace(
        /(trigger:\s*none\s*\n)/,
        `$1\nschedules:${newSchedule}\n\n`
      );
    }

    // Step 5: Update the YAML file content in the repository
    await updateYamlFileContentSchedule(accessToken, repoId, yamlPath, yamlContent);

    console.log('Schedule replaced successfully and YAML file updated.');
    return {
      success: true,
      message: 'Schedule replaced and YAML file updated successfully',
    };
  } catch (error) {
    console.error('Error updating client schedule:', error);
    throw error;
  }
};


const updateYamlFileContentSchedule = async (accessToken, repoId, path, content) => {
  setAuthHeader(accessToken);

  // Step 1: Get the latest commit ID
  const repoUrl = `https://dev.azure.com/cyaniccloud/_apis/git/repositories/${repoId}`;
  const refsUrl = `${repoUrl}/refs?filter=heads/main&api-version=6.0`;

  try {
    // Get the latest commit ID
    const refsResponse = await axiosInstance.get(refsUrl);
    const commitId = refsResponse.data.value[0].objectId;

    // Step 2: Create a new commit with the updated content
    const updateUrl = `${repoUrl}/pushes?api-version=6.0`;
    const body = {
      "refUpdates": [
        {
          "name": "refs/heads/main",
          "oldObjectId": commitId
        }
      ],
      "commits": [
        {
          "comment": "Updating YAML file with client schedule",
          "changes": [
            {
              "changeType": "edit",
              "item": {
                "path": path
              },
              "newContent": {
                "content": content,
                "contentType": "rawtext"
              }
            }
          ]
        }
      ]
    };

    // Make the request to update the YAML file
    await axiosInstance.post(updateUrl, body);
    console.log("YAML file updated successfully.");
  } catch (error) {
    console.error("Error updating YAML file:", error);
    throw error;
  }
};

// Fetch all backup tags for a specific client
const fetchClientBackups = async (accessToken, projectId, clientCode, includeSize = false) => {
  setAuthHeader(accessToken);

  const repoId = '62b297c6-760c-4250-a926-52b8850e6d5f';

  try {
    const apiUrl = `https://dev.azure.com/cyaniccloud/${projectId}/_apis/git/repositories/${repoId}/refs?filter=tags/backup/${clientCode}&api-version=7.0`;
    const response = await axiosInstance.get(apiUrl);

    // Transform the refs into a more usable backup format
    const backups = await Promise.all(response.data.value.map(async (ref) => {
      const tagName = ref.name.replace('refs/tags/', '');

      // Parse date from tag name
      const parts = tagName.split('/');
      let parsedDate = new Date('Invalid Date');

      if (parts.length >= 3) {
        const timestamp = parts[parts.length - 1];

        if (/^\d{8}_\d{6}$/.test(timestamp)) {
          const year = parseInt(timestamp.substring(0, 4));
          const month = parseInt(timestamp.substring(4, 6)) - 1;
          const day = parseInt(timestamp.substring(6, 8));
          const hour = parseInt(timestamp.substring(9, 11));
          const minute = parseInt(timestamp.substring(11, 13));
          const second = parseInt(timestamp.substring(13, 15));

          parsedDate = new Date(year, month, day, hour, minute, second);
        }
      }

      // Calculate size if requested
      let size = 'N/A';
      if (includeSize) {
        size = await calculateBackupSize(accessToken, projectId, repoId, ref.objectId);
      }

      return {
        id: ref.objectId,
        tagName: tagName,
        clientCode: clientCode,
        date: parsedDate,
        type: 'Scheduled',
        size: size,
        ref: ref
      };
    }));

    // Filter and sort
    const validBackups = backups.filter(backup =>
      backup.date && backup.date.toString() !== 'Invalid Date'
    );

    return validBackups.sort((a, b) => b.date - a.date);

  } catch (error) {
    console.error('Error fetching client backups:', error);
    throw error;
  }
};
// Get backup content by tag
const fetchBackupContent = async (accessToken, projectId, repoId, tagName) => {
  setAuthHeader(accessToken);

  try {
    // First get the commit SHA for the tag
    const tagUrl = `https://dev.azure.com/cyaniccloud/${projectId}/_apis/git/repositories/${repoId}/refs?filter=tags/${tagName}&api-version=7.0`;
    const tagResponse = await axiosInstance.get(tagUrl);

    if (!tagResponse.data.value || tagResponse.data.value.length === 0) {
      throw new Error('Tag not found');
    }

    const commitId = tagResponse.data.value[0].objectId;

    // Then fetch the tree for that commit
    const treeUrl = `https://dev.azure.com/cyaniccloud/${projectId}/_apis/git/repositories/${repoId}/trees/${commitId}?recursive=true&api-version=7.0`;
    const treeResponse = await axiosInstance.get(treeUrl);

    return treeResponse.data;
  } catch (error) {
    console.error('Error fetching backup content:', error);
    throw error;
  }
};

// Restore from backup
const restoreFromBackup = async (accessToken, projectId, repoId, tagName, clientCode) => {
  setAuthHeader(accessToken);

  try {
    // Get the commit SHA for the backup tag
    const tagUrl = `https://dev.azure.com/cyaniccloud/${projectId}/_apis/git/repositories/${repoId}/refs?filter=tags/${tagName}&api-version=7.0`;
    const tagResponse = await axiosInstance.get(tagUrl);

    if (!tagResponse.data.value || tagResponse.data.value.length === 0) {
      throw new Error('Backup tag not found');
    }

    const backupCommitId = tagResponse.data.value[0].objectId;

    // Create a new branch from main
    const branchName = `restore/${clientCode}/${tagName.replace(/[^a-zA-Z0-9-]/g, '-')}`;
    const createBranchUrl = `https://dev.azure.com/cyaniccloud/${projectId}/_apis/git/repositories/${repoId}/refs?api-version=7.0`;

    // Create the restore branch directly from the backup commit
    const createBranchBody = [{
      name: `refs/heads/${branchName}`,
      newObjectId: backupCommitId,
      oldObjectId: "0000000000000000000000000000000000000000"
    }];

    await axiosInstance.post(createBranchUrl, createBranchBody);

    // Create a pull request
    const prUrl = `https://dev.azure.com/cyaniccloud/${projectId}/_apis/git/pullrequests?api-version=7.0`;
    const prBody = {
      sourceRefName: `refs/heads/${branchName}`,
      targetRefName: "refs/heads/main",
      title: `Restore ${clientCode} from backup ${tagName}`,
      description: `Restoring configuration for ${clientCode} from backup taken at ${tagName}`,
      reviewers: []
    };

    const prResponse = await axiosInstance.post(prUrl, prBody);
    return prResponse.data;

  } catch (error) {
    console.error('Error restoring from backup:', error);
    throw error;
  }
};

// Delete old backups (retention policy)
const deleteOldBackups = async (accessToken, projectId, repoId, clientCode, daysToKeep = 30) => {
  try {
    const backups = await fetchClientBackups(accessToken, projectId, clientCode);
    const cutoffDate = new Date();
    cutoffDate.setDate(cutoffDate.getDate() - daysToKeep);

    const backupsToDelete = backups.filter(backup => backup.date < cutoffDate);

    for (const backup of backupsToDelete) {
      const deleteUrl = `https://dev.azure.com/cyaniccloud/${projectId}/_apis/git/repositories/${repoId}/refs?filter=tags/${backup.tagName}&api-version=7.0`;
      await axiosInstance.delete(deleteUrl);
    }

    return backupsToDelete.length;
  } catch (error) {
    console.error('Error deleting old backups:', error);
    throw error;
  }
};

const calculateBackupSize = async (accessToken, projectId, repoId, commitId) => {
  setAuthHeader(accessToken);

  try {
    // Get the tree for this commit
    const treeUrl = `https://dev.azure.com/cyaniccloud/${projectId}/_apis/git/repositories/${repoId}/commits/${commitId}?api-version=7.0`;
    const commitResponse = await axiosInstance.get(treeUrl);
    const treeId = commitResponse.data.treeId;

    // Get all items in the tree
    const itemsUrl = `https://dev.azure.com/cyaniccloud/${projectId}/_apis/git/repositories/${repoId}/trees/${treeId}?recursive=true&api-version=7.0`;
    const itemsResponse = await axiosInstance.get(itemsUrl);

    // Calculate total size
    let totalSize = 0;
    for (const item of itemsResponse.data.treeEntries) {
      if (item.gitObjectType === 'blob') {
        totalSize += item.size || 0;
      }
    }

    // Format size for display
    return formatFileSize(totalSize);
  } catch (error) {
    console.error('Error calculating backup size:', error);
    return 'N/A';
  }
};

// Helper function to format file sizes
const formatFileSize = (bytes) => {
  if (bytes === 0) return '0 Bytes';

  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

// ============================================================================
// 1. GET CLIENT CAPTURE BRANCHES
// ============================================================================

/**
 * Get all capture branches for a client
 * Returns branches sorted by date with metadata
 */
const getClientBranches = async (accessToken, repoId, clientCode) => {
  const headers = {
    'Authorization': `Bearer ${accessToken}`
  };

  try {
    // Get all branches
    const response = await axios.get(
      `${apiUrlBase}/1b238677-4a8d-4243-827e-88a0558c085a/_apis/git/repositories/${repoId}/refs`,
      {
        headers,
        params: {
          'filter': `heads/client-${clientCode}`,
          'api-version': '7.0'
        }
      }
    );

    // Transform to useful format
    const branches = await Promise.all(
      response.data.value.map(async (ref) => {
        const branchName = ref.name.replace('refs/heads/', '');

        // Get latest commit for this branch
        const commit = await axios.get(
          `${apiUrlBase}/1b238677-4a8d-4243-827e-88a0558c085a/_apis/git/repositories/${repoId}/commits/${ref.objectId}`,
          { headers, params: { 'api-version': '7.0' } }
        );

        return {
          name: branchName,
          objectId: ref.objectId,
          date: commit.data.author.date,
          author: commit.data.author.name,
          message: commit.data.comment,
          merged: false, // TODO: Check if merged
          metadata: null // TODO: Load from .capture-metadata.json if exists
        };
      })
    );

    // Sort by date (newest first)
    return branches.sort((a, b) => new Date(b.date) - new Date(a.date));

  } catch (error) {
    console.error('Error fetching client branches:', error);
    return [];
  }
};

// ============================================================================
// 2. GET CLIENT OVERVIEW STATS
// ============================================================================

/**
 * Get overview statistics for a client
 */
const getClientOverview = async (accessToken, repoId, clientCode) => {
  try {
    // Get active branches and completed PRs in parallel
    const [branches, mergedPRs] = await Promise.all([
      getClientBranches(accessToken, repoId, clientCode),
      getClientMergedPRs(accessToken, repoId, clientCode)
    ]);

    // Last capture date could be from:
    // 1. Most recent active branch, OR
    // 2. Most recent merged PR creation date
    let lastCaptureDate = null;

    if (branches.length > 0) {
      lastCaptureDate = branches[0].date; // Most recent active branch
    }

    // Also check merged PRs creation date
    if (mergedPRs.length > 0) {
      const lastMergedPRDate = new Date(mergedPRs[0].creationDate);

      if (!lastCaptureDate || lastMergedPRDate > new Date(lastCaptureDate)) {
        lastCaptureDate = mergedPRs[0].creationDate;
      }
    }

    // Last merge = most recent completed PR
    const lastMergedPR = mergedPRs[0];

    // Pending reviews = number of active branches (unmerged)
    const pendingReviews = branches.length;

    return {
      lastCapture: lastCaptureDate,
      lastMerge: lastMergedPR ? lastMergedPR.closedDate : null,
      pendingReviews
    };

  } catch (error) {
    console.error('Error fetching client overview:', error);
    return {
      lastCapture: null,
      lastMerge: null,
      pendingReviews: 0
    };
  }
};

/**
 * Get completed (merged) pull requests for a client
 * Returns PRs sorted by completion date (newest first)
 */
const getClientMergedPRs = async (accessToken, repoId, clientCode) => {
  const headers = {
    'Authorization': `Bearer ${accessToken}`
  };

  try {
    // Query for ALL completed PRs (can't filter by partial branch name in API)
    const response = await axios.get(
      `${apiUrlBase}/1b238677-4a8d-4243-827e-88a0558c085a/_apis/git/repositories/${repoId}/pullrequests`,
      {
        headers,
        params: {
          'searchCriteria.status': 'completed',
          'searchCriteria.targetRefName': 'refs/heads/main', // Only PRs merged to main
          '$top': 1000, // Get more PRs to ensure we find client's
          'api-version': '7.0'
        }
      }
    );

    // Filter for this client's branches (client-{CODE}-capture-*)
    const clientBranchPattern = `refs/heads/client-${clientCode}`;

    const completedPRs = response.data.value
      .filter(pr =>
        pr.status === 'completed' &&
        pr.closedDate &&
        pr.sourceRefName &&
        pr.sourceRefName.startsWith(clientBranchPattern)
      )
      .sort((a, b) => new Date(b.closedDate) - new Date(a.closedDate));

    return completedPRs;

  } catch (error) {
    console.error('Error fetching merged PRs:', error);
    return [];
  }
};

// ============================================================================
// 3. DETECT CLIENT DRIFT (Git-based comparison)
// ============================================================================

/**
 * Compare client branch vs main to detect drift
 * This is FAST - uses Git API (1 call)
 */
const detectClientDrift = async (accessToken, repoId, clientCode, branchName) => {
  const headers = {
    'Authorization': `Bearer ${accessToken}`
  };

  try {
    const response = await axios.get(
      `${apiUrlBase}/1b238677-4a8d-4243-827e-88a0558c085a/_apis/git/repositories/${repoId}/diffs/commits`,
      {
        headers,
        params: {
          'baseVersion': 'main',
          'targetVersion': branchName,
          'api-version': '7.0'
        }
      }
    );

    // FIX: Change from /M365Config/Clients/${clientCode} to /Clients/${clientCode}
    const clientPath = `/Clients/${clientCode}`;  // ← UPDATED
    const changes = (response.data.changes || []).filter(change =>
      change.item.path.startsWith(clientPath) &&
      change.item.path.endsWith('.json')
    );

    return {
      changeCounts: response.data.changeCounts || { Add: 0, Edit: 0, Delete: 0 },
      changes: changes,
      summary: {
        unchanged: response.data.changeCounts.Add === 0 && response.data.changeCounts.Edit === 0 ?
          'All' : 'Unknown',
        modified: response.data.changeCounts.Edit || 0,
        added: response.data.changeCounts.Add || 0,
        deleted: response.data.changeCounts.Delete || 0
      }
    };

  } catch (error) {
    console.error('Error detecting drift:', error);
    return {
      changeCounts: { Add: 0, Edit: 0, Delete: 0 },
      changes: [],
      summary: { unchanged: 0, modified: 0, added: 0, deleted: 0 }
    };
  }
};

// ============================================================================
// 4. COMPARE BASELINE VS CLIENT (Optimized JS comparison)
// ============================================================================

/**
 * Compare baseline vs client configuration
 * This uses your existing comparison logic but optimized
 */
const compareBaselineVsClientOptimized = async (
  accessToken,
  repoId,
  clientCode,
  branch = 'main',
  excludedProperties = [],
  excludedPaths = []
) => {
  try {
    const baselineDir = '/M365Config/Resources';
    const clientDir = `/Clients/${clientCode}/M365Config/Resources`;

    const [baselineTree, clientTree] = await Promise.all([
      getDirectoryTreeOptimized(accessToken, repoId, baselineDir, 'main'),
      getDirectoryTreeOptimized(accessToken, repoId, clientDir, branch)
    ]);

    // Build file maps with path filtering
    const baselineMap = new Map();
    const clientMap = new Map();

    const normalizedExcludedPaths = excludedPaths.map(p =>
      (p.startsWith("/") ? p : "/" + p).replace(/\/+$/, "").toLowerCase()
    );

    const isPathExcluded = (path) => {
      const normalizedPath = path.toLowerCase();
      return normalizedExcludedPaths.some(excludedPath =>
        normalizedPath.startsWith(excludedPath)
      );
    };

    baselineTree.forEach(file => {
      const relativePath = file.path.replace(baselineDir, '');
      if (!isPathExcluded(file.path)) {
        baselineMap.set(relativePath, file);
      }
    });

    clientTree.forEach(file => {
      const relativePath = file.path.replace(clientDir, '');
      if (!isPathExcluded(file.path)) {
        clientMap.set(relativePath, file);
      }
    });

    // Determine files to compare and track missing
    const filesToCompare = [];
    const identicalFiles = [];
    const missingPolicies = [];

    baselineMap.forEach((baselineFile, relativePath) => {
      const clientFile = clientMap.get(relativePath);
      if (clientFile) {
        // Quick skip: same objectId = identical content, no comparison needed
        if (baselineFile.objectId === clientFile.objectId) {
          identicalFiles.push({
            relativePath,
            baselinePath: baselineFile.path,
            clientPath: clientFile.path
          });
        } else {
          filesToCompare.push({
            relativePath,
            baselinePath: baselineFile.path,
            clientPath: clientFile.path
          });
        }
      } else {
        missingPolicies.push({
          relativePath,
          baselinePath: baselineFile.path,
          policyName: baselineFile.path.split('/').pop().replace('.json', ''),
          category: getCategoryFromPath(baselineFile.path)
        });
      }
    });

    //  NEW: Track client-only policies (exist in client but not in baseline)
    const clientOnlyPolicies = [];
    clientMap.forEach((clientFile, relativePath) => {
      if (!baselineMap.has(relativePath)) {
        clientOnlyPolicies.push({
          relativePath,
          clientPath: clientFile.path,
          policyName: clientFile.path.split('/').pop().replace('.json', ''),
          category: getCategoryFromPath(clientFile.path)
        });
      }
    });

    console.log(`📊 Comparison summary:
      - Identical files (skipped): ${identicalFiles.length}
      - Files to compare: ${filesToCompare.length}
      - Missing from client: ${missingPolicies.length}
      - Client-only policies: ${clientOnlyPolicies.length}`);

    // Fetch content for missing policies
    console.log(`📥 Fetching content for ${missingPolicies.length} missing policies...`);

    const missingPoliciesWithContent = await Promise.all(
      missingPolicies.map(async (missing) => {
        try {
          const content = await fetchFileContent(accessToken, repoId, missing.baselinePath, 'main');
          return {
            ...missing,
            baselinePolicy: JSON.parse(content)
          };
        } catch (error) {
          console.error(`Failed to fetch ${missing.baselinePath}:`, error);
          return {
            ...missing,
            baselinePolicy: null
          };
        }
      })
    );

    //  NEW: Fetch content for client-only policies
    console.log(`📥 Fetching content for ${clientOnlyPolicies.length} client-only policies...`);

    const clientOnlyPoliciesWithContent = await Promise.all(
      clientOnlyPolicies.map(async (clientOnly) => {
        try {
          const content = await fetchFileContent(accessToken, repoId, clientOnly.clientPath, branch);
          return {
            ...clientOnly,
            clientPolicy: JSON.parse(content)
          };
        } catch (error) {
          console.error(`Failed to fetch ${clientOnly.clientPath}:`, error);
          return {
            ...clientOnly,
            clientPolicy: null
          };
        }
      })
    );

    // Fetch and compare in batches
    const batchSize = 20;
    let totalCompliant = 0;
    let totalDeviations = 0;
    let criticalCount = 0;
    let highCount = 0;
    let mediumCount = 0;
    let prerequisiteCount = 0

    const deviations = [];

    for (let i = 0; i < filesToCompare.length; i += batchSize) {
      const batch = filesToCompare.slice(i, i + batchSize);

      const batchResults = await Promise.all(
        batch.map(file => compareFilePairSimple(
          accessToken,
          repoId,
          file.baselinePath,
          file.clientPath,
          'main',
          branch,
          excludedProperties
        ))
      );
      batchResults.forEach(result => {
        //  NEW: Check if this path is a prerequisite
        const isPrerequisite = isBaselinePrerequisite(result.path);

        if (result.deviationCount === 0) {
          console.log(` Checking deviation path for prerequisite: ${result.path}`, isPrerequisite ? ' PREREQUISITE' : ' NOT PREREQUISITE');
          totalCompliant++;
        } else {
          totalDeviations++;

          //  NEW: Count as prerequisite instead of severity-based if it's a prerequisite path
          if (isPrerequisite) {
            prerequisiteCount++;
          } else {
            // Only count by severity if NOT a prerequisite
            result.deviations.forEach(dev => {
              if (dev.severity === 'critical') criticalCount++;
              else if (dev.severity === 'high') highCount++;
              else if (dev.severity === 'medium') mediumCount++;
            });
          }

          deviations.push({
            path: result.path,
            policyName: result.policyName,
            category: result.category,
            count: result.deviationCount,
            severity: isPrerequisite ? 'prerequisite' : result.highestSeverity,
            deviations: result.deviations.map(dev => ({
              ...dev,
              severity: isPrerequisite ? 'prerequisite' : dev.severity,
              isPrerequisite: isPrerequisite
            })),
            baselinePolicy: result.baselinePolicy,
            clientPolicy: result.clientPolicy,
            deviationCount: result.deviationCount,
            highestSeverity: isPrerequisite ? 'prerequisite' : result.highestSeverity,
            isPrerequisite: isPrerequisite,
          });
        }
      });
    }

    missingPoliciesWithContent.forEach(missing => {
      // Check if this is a baseline prerequisite
      console.log(' Checking path for prerequisite:', missing.baselinePath);
      const isPrerequisite = isBaselinePrerequisite(missing.baselinePath);

      console.log('  Result:', isPrerequisite ? ' PREREQUISITE' : ' NOT PREREQUISITE');


      if (isPrerequisite) {
        prerequisiteCount++;
      } else {
        criticalCount++;
      }

      totalDeviations++;

      deviations.push({
        path: missing.baselinePath,
        policyName: missing.policyName,
        category: missing.category,
        count: 1,
        severity: isPrerequisite ? 'prerequisite' : 'critical',
        deviations: [{
          property: 'ENTIRE_POLICY',
          baselineValue: 'Policy exists in baseline',
          clientValue: isPrerequisite
            ? 'PREREQUISITE - This baseline-specific policy enables regional deployment but is not a security requirement'
            : 'MISSING - Policy does not exist in client configuration',
          severity: isPrerequisite ? 'prerequisite' : 'critical',
          isMissing: true,
          isPrerequisite: isPrerequisite
        }],
        highestSeverity: isPrerequisite ? 'prerequisite' : 'critical',
        isMissing: true,
        isPrerequisite: isPrerequisite,
        baselinePolicy: missing.baselinePolicy,
        clientPolicy: null,
        deviationCount: 1,
        matchStatus: 'unmatched',
        changeType: 'missing',
      });
    });

    return {
      totalCount: identicalFiles.length + filesToCompare.length + missingPoliciesWithContent.length,
      compliantCount: totalCompliant + identicalFiles.length,
      deviationCount: totalDeviations,
      criticalCount,
      highCount,
      mediumCount,
      prerequisiteCount,
      deviations,
      missingPoliciesCount: missingPoliciesWithContent.length,
      //  NEW: Include client-only policies for matching pool
      clientOnlyPolicies: clientOnlyPoliciesWithContent.filter(p => p.clientPolicy),
    };

  } catch (error) {
    console.error('Error comparing baseline vs client:', error);
    throw error;
  }
};

/**
 * Helper: Get directory tree
 */
const getDirectoryTreeOptimized = async (accessToken, repoId, path, branch = 'main') => {
  const headers = {
    'Authorization': `Bearer ${accessToken}`
  };

  const response = await axios.get(
    `${apiUrlBase}/1b238677-4a8d-4243-827e-88a0558c085a/_apis/git/repositories/${repoId}/items`,
    {
      headers,
      params: {
        'scopePath': path,
        'versionDescriptor.version': branch,
        'versionDescriptor.versionType': 'branch',
        'recursionLevel': 'Full',
        'api-version': '7.0'
      }
    }
  );

  // Include objectId for content dedup
  return response.data.value
    .filter(item => !item.isFolder && item.path.endsWith('.json'))
    .map(item => ({ path: item.path, objectId: item.objectId }));
};


const decodeBase64UTF8 = (contentString) => {
  try {
    // Check if content is already plain text JSON (starts with { or [)
    const trimmed = contentString.trim();
    if (trimmed.startsWith('{') || trimmed.startsWith('[')) {
      // Already plain text JSON, return as-is
      return contentString;
    }

    // Otherwise, decode from base64
    // Clean the base64 string: remove whitespace, newlines, and carriage returns
    const cleanedBase64 = contentString.replace(/[\r\n\s]/g, '');

    // Handle URL-safe base64 (replace - with + and _ with /)
    const standardBase64 = cleanedBase64.replace(/-/g, '+').replace(/_/g, '/');

    // Add padding if needed
    const paddedBase64 = standardBase64.padEnd(
      standardBase64.length + ((4 - (standardBase64.length % 4)) % 4),
      '='
    );

    // Decode base64 to binary string
    const binaryString = atob(paddedBase64);

    // Convert binary string to byte array
    const bytes = new Uint8Array(binaryString.length);
    for (let i = 0; i < binaryString.length; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }

    // Decode UTF-8 bytes to string
    const decoder = new TextDecoder('utf-8');
    return decoder.decode(bytes);
  } catch (error) {
    console.error('Error decoding content:', error);
    console.error('Content string length:', contentString?.length);
    console.error('First 100 chars:', contentString?.substring(0, 100));
    throw error;
  }
};


/**
 * Normalize a value for comparison
 * - Converts boolean strings to lowercase (PowerShell "True" vs JavaScript "true")
 * - Handles null/undefined consistently
 */
const normalizeValue = (value) => {
  // Handle null/undefined
  if (value === null || value === undefined) {
    return null;
  }

  // Handle boolean strings (PowerShell vs JavaScript)
  if (typeof value === 'string') {
    const lower = value.toLowerCase();
    if (lower === 'true' || lower === 'false') {
      return lower;
    }
  }

  // Handle actual booleans
  if (typeof value === 'boolean') {
    return value.toString().toLowerCase();
  }

  return value;
};

/**
 * Deep normalize an object (recursively normalize all values)
 */
const deepNormalizeObject = (obj) => {
  if (obj === null || obj === undefined) {
    return obj;
  }

  if (Array.isArray(obj)) {
    return obj.map(item => deepNormalizeObject(item));
  }

  if (typeof obj === 'object') {
    const normalized = {};
    for (const [key, value] of Object.entries(obj)) {
      normalized[key] = deepNormalizeObject(value);
    }
    return normalized;
  }

  return normalizeValue(obj);
};


/**
 * Enhanced deep comparison that handles arrays intelligently
 */
const compareJsonWithArraySupport = (baseline, client, path = '', excludedProperties = []) => {
  const deviations = [];

  //  Normalize both inputs before comparing
  const normalizedBaseline = deepNormalizeObject(baseline);
  const normalizedClient = deepNormalizeObject(client);

  // If both are arrays, compare item-by-item
  if (Array.isArray(normalizedBaseline) && Array.isArray(normalizedClient)) {
    //  Pass current path as context to findArrayKeyField
    const keyField = findArrayKeyField(normalizedBaseline, normalizedClient, path);

    if (keyField) {
      // Match items by key and compare
      const baselineMap = new Map(normalizedBaseline.map(item => [item[keyField], item]));
      const clientMap = new Map(normalizedClient.map(item => [item[keyField], item]));

      // Check all keys from both arrays
      const allKeys = new Set([...baselineMap.keys(), ...clientMap.keys()]);

      allKeys.forEach(key => {
        const baseItem = baselineMap.get(key);
        const clientItem = clientMap.get(key);

        if (!baseItem) {
          // Item only in client (added)
          deviations.push({
            property: `${path}[${keyField}=${key}]`,
            baselineValue: undefined,
            clientValue: clientItem,
            severity: 'low',
            changeType: 'added'
          });
        } else if (!clientItem) {
          // Item only in baseline (removed)
          deviations.push({
            property: `${path}[${keyField}=${key}]`,
            baselineValue: baseItem,
            clientValue: undefined,
            severity: 'medium',
            changeType: 'removed'
          });
        } else {
          // Both exist - compare recursively
          const subDeviations = compareJsonWithArraySupport(
            baseItem,
            clientItem,
            `${path}[${keyField}=${key}]`,
            excludedProperties
          );
          deviations.push(...subDeviations);
        }
      });
    } else {
      //  No key field - compare by position (index)
      //console.log(`📍 Comparing ${path} by position (no key field)`);

      const maxLength = Math.max(normalizedBaseline.length, normalizedClient.length);

      for (let i = 0; i < maxLength; i++) {
        const baseItem = normalizedBaseline[i];
        const clientItem = normalizedClient[i];

        if (!baseItem) {
          // Item only in client (added)
          deviations.push({
            property: `${path}[${i}]`,
            baselineValue: undefined,
            clientValue: clientItem,
            severity: 'low',
            changeType: 'added'
          });
        } else if (!clientItem) {
          // Item only in baseline (removed)
          deviations.push({
            property: `${path}[${i}]`,
            baselineValue: baseItem,
            clientValue: undefined,
            severity: 'medium',
            changeType: 'removed'
          });
        } else {
          // Both exist - compare recursively
          const subDeviations = compareJsonWithArraySupport(
            baseItem,
            clientItem,
            `${path}[${i}]`,
            excludedProperties
          );
          deviations.push(...subDeviations);
        }
      }
    }
  }
  // If both are objects, compare property-by-property
  else if (
    typeof normalizedBaseline === 'object' &&
    normalizedBaseline !== null &&
    typeof normalizedClient === 'object' &&
    normalizedClient !== null &&
    !Array.isArray(normalizedBaseline) &&
    !Array.isArray(normalizedClient)
  ) {
    const allKeys = new Set([
      ...Object.keys(normalizedBaseline),
      ...Object.keys(normalizedClient)
    ]);

    allKeys.forEach(key => {
      const propertyPath = path ? `${path}.${key}` : key;

      // Skip excluded properties
      if (shouldExcludeProperty(propertyPath, excludedProperties)) {
        return;
      }

      const baseValue = normalizedBaseline[key];
      const clientValue = normalizedClient[key];

      // Recursively compare
      const subDeviations = compareJsonWithArraySupport(
        baseValue,
        clientValue,
        propertyPath,
        excludedProperties
      );
      deviations.push(...subDeviations);
    });
  }
  // Primitive values - direct comparison
  else {
    if (JSON.stringify(normalizedBaseline) !== JSON.stringify(normalizedClient)) {
      deviations.push({
        property: path,
        baselineValue: baseline, //  Return original for display
        clientValue: client,     //  Return original for display
        severity: determineSeverity(path),
        changeType: 'modified'
      });
    }
  }

  return deviations;
};


/**
 * Find a suitable key field for array matching
 * CRITICAL: Avoid using tenant-specific IDs (like group GUIDs) as keys
 */
const findArrayKeyField = (arr1, arr2, parentPath = '') => {
  //  CRITICAL: For includeTargets/excludeTargets, NEVER use "id" as it's a group GUID
  // These arrays usually have only 1-2 items anyway, so compare by position
  if (parentPath.includes('includeTargets') ||
    parentPath.includes('excludeTargets') ||
    parentPath.includes('includeTarget') ||
    parentPath.includes('excludeTarget')) {

    // Try to find a non-ID key
    const safeKeys = ['targetType', 'name', 'displayName', '@odata.type'];

    for (const key of safeKeys) {
      const baselineHasKey = arr1.filter(item => item && item[key] !== undefined);
      const baselineUniqueValues = new Set(baselineHasKey.map(item => item[key]));

      const clientHasKey = arr2.filter(item => item && item[key] !== undefined);
      const clientUniqueValues = new Set(clientHasKey.map(item => item[key]));

      const baselineIsUnique = baselineHasKey.length > 0 &&
        baselineUniqueValues.size === baselineHasKey.length;
      const clientIsUnique = clientHasKey.length > 0 &&
        clientUniqueValues.size === clientHasKey.length;

      if (baselineIsUnique && clientIsUnique) {
        console.log(` Using safe key "${key}" for ${parentPath}`);
        return key;
      }
    }

    // No safe key found - return null to compare by position
    console.log(`⚠️ No safe key for ${parentPath} - comparing by position`);
    return null;
  }

  // For other arrays, use standard key detection
  const possibleKeys = [
    'id', 'Id', 'ID',
    'settingDefinitionId',
    'name', 'Name',
    'displayName', 'DisplayName',
    'Identity',
    'Guid', 'guid',
    '@odata.type',
    'RuleId',
    'RuleName'
  ];

  if (!arr1 || arr1.length === 0 || !arr2 || arr2.length === 0) {
    return null;
  }

  for (const key of possibleKeys) {
    const baselineHasKey = arr1.filter(item => item && item[key] !== undefined);
    const baselineUniqueValues = new Set(baselineHasKey.map(item => item[key]));

    const clientHasKey = arr2.filter(item => item && item[key] !== undefined);
    const clientUniqueValues = new Set(clientHasKey.map(item => item[key]));

    const baselineIsUnique = baselineHasKey.length > 0 &&
      baselineUniqueValues.size === baselineHasKey.length;
    const clientIsUnique = clientHasKey.length > 0 &&
      clientUniqueValues.size === clientHasKey.length;

    if (baselineIsUnique && clientIsUnique) {
      return key;
    }
  }

  return null;
};

/**
 * Check if property should be excluded
 */
/**
 * Check if property should be excluded
 * Handles both simple property names (e.g., "id") and full paths (e.g., "conditions.users.excludeUsers")
 */
const shouldExcludeProperty = (propertyPath, excludedProperties) => {
  if (!propertyPath) return false;

  const lowerPath = propertyPath.toLowerCase();

  return excludedProperties.some(excluded => {
    const lowerExcluded = excluded.toLowerCase();

    //  1. Exact match (case-insensitive)
    if (lowerPath === lowerExcluded) return true;

    //  2. Path ends with the excluded property
    // Handles nested paths like "grantControls.authenticationStrength@odata.context"
    if (lowerPath.endsWith(`.${lowerExcluded}`)) return true;

    //  3. Handle array indices in the path
    // Remove array indices like [id=123] or [0] to compare base path
    const pathWithoutArrays = lowerPath.replace(/\[([^\]]+)\]/g, '');
    const excludedWithoutArrays = lowerExcluded.replace(/\[([^\]]+)\]/g, '');

    if (pathWithoutArrays === excludedWithoutArrays) return true;
    if (pathWithoutArrays.endsWith(`.${excludedWithoutArrays}`)) return true;

    //  4. Check if excluded property is a parent path of the current property
    // Example: excluded="conditions.users" should exclude "conditions.users.excludeUsers"
    if (lowerPath.startsWith(`${lowerExcluded}.`)) return true;

    return false;
  });
};

/**
 * Determine severity based on property path
 */
const determineSeverity = (propertyPath) => {
  const lowerPath = propertyPath.toLowerCase();

  // Critical: Security and authentication related
  if (
    lowerPath.includes('security') ||
    lowerPath.includes('password') ||
    lowerPath.includes('encryption') ||
    lowerPath.includes('authentication') ||
    lowerPath.includes('credential') ||
    lowerPath.includes('token') ||
    lowerPath.includes('secret')
  ) {
    return 'critical';
  }

  // High: Important configuration and policy settings
  if (
    lowerPath.includes('enabled') ||
    lowerPath.includes('disabled') ||
    lowerPath.includes('policy') ||
    lowerPath.includes('rule') ||
    lowerPath.includes('permission') ||
    lowerPath.includes('access') ||
    lowerPath.includes('compliance')
  ) {
    return 'high';
  }

  // Medium: General settings
  if (
    lowerPath.includes('config') ||
    lowerPath.includes('setting')
  ) {
    return 'medium';
  }

  return 'low';
};


/**
 * Helper: Compare a single file pair
 */

const compareFilePairSimple = async (
  accessToken,
  repoId,
  baselinePath,
  clientPath,
  baselineBranch = 'main',
  clientBranch = 'main',
  excludedProperties = []
) => {
  const headers = {
    'Authorization': `Bearer ${accessToken}`
  };

  try {
    // Initialize file content caches (module-scoped, persists across comparison runs)
    if (!compareFilePairSimple._fileCache) {
      compareFilePairSimple._fileCache = new Map();
    }

    const fetchFile = async (path, branch) => {
      const key = `${repoId}:${branch}:${path}`;
      const cached = compareFilePairSimple._fileCache.get(key);
      if (cached !== undefined) return { data: { content: cached } };

      const resp = await axios.get(
        `${apiUrlBase}/1b238677-4a8d-4243-827e-88a0558c085a/_apis/git/repositories/${repoId}/items`,
        {
          headers,
          params: {
            'path': path,
            'versionDescriptor.version': branch,
            'versionDescriptor.versionType': 'branch',
            'includeContent': true,
            'api-version': '7.0'
          }
        }
      );

      compareFilePairSimple._fileCache.set(key, resp.data.content);
      return resp;
    };

    const [baselineResp, clientResp] = await Promise.all([
      fetchFile(baselinePath, baselineBranch),
      fetchFile(clientPath, clientBranch)
    ]);

    // Safety check: Ensure content exists before decoding
    if (!baselineResp.data.content || !clientResp.data.content) {
      throw new Error(`Missing content for ${baselinePath} or ${clientPath}`);
    }

    // Decode content with proper UTF-8 support
    const baselineJson = JSON.parse(decodeBase64UTF8(baselineResp.data.content));
    const clientJson = JSON.parse(decodeBase64UTF8(clientResp.data.content));

    // Use enhanced comparison with array support
    const deviations = compareJsonWithArraySupport(
      baselineJson,
      clientJson,
      '',
      excludedProperties
    );

    // Determine highest severity
    let highestSeverity = 'low';
    if (deviations.some(d => d.severity === 'critical')) {
      highestSeverity = 'critical';
    } else if (deviations.some(d => d.severity === 'high')) {
      highestSeverity = 'high';
    } else if (deviations.some(d => d.severity === 'medium')) {
      highestSeverity = 'medium';
    }

    return {
      path: clientPath,
      policyName: clientPath.split('/').pop().replace('.json', ''),
      category: getCategoryFromPath(clientPath),
      deviationCount: deviations.length,
      deviations,
      highestSeverity,
      //  NEW: Include full policy objects for intelligent matching
      baselinePolicy: baselineJson,
      clientPolicy: clientJson,
    };

  } catch (error) {
    console.error(`Error comparing ${baselinePath}:`, error);
    return {
      path: clientPath,
      policyName: 'Error',
      category: 'Error',
      deviationCount: 0,
      deviations: [],
      highestSeverity: 'low',
      //  NEW: Include nulls on error
      baselinePolicy: null,
      clientPolicy: null,
    };
  }
};


const getCategoryFromPath = (path) => {
  // Extract category from path structure: /M365Config/Resources/{Category}/...
  // or /Clients/{ClientCode}/M365Config/Resources/{Category}/...

  const match = path.match(/\/M365Config\/Resources\/([^/]+)/i);
  if (match && match[1]) {
    return match[1]; // Returns the folder name after Resources/
  }
  return 'Other';
};

// ============================================================================
// 5. MERGE CAPTURE TO MAIN
// ============================================================================


/**
 * Create a pull request for merging capture to main
 * Note: Does NOT auto-complete - respects Azure DevOps branch policies
 */
const mergeCaptureToMain = async (
  accessToken,
  repoId,
  branchName,
  clientCode,
  approvalData
) => {
  const headers = {
    'Authorization': `Bearer ${accessToken}`,
    'Content-Type': 'application/json'
  };

  try {
    // Create Pull Request (but don't auto-complete it)
    const prResponse = await axios.post(
      `${apiUrlBase}/1b238677-4a8d-4243-827e-88a0558c085a/_apis/git/repositories/${repoId}/pullrequests`,
      {
        sourceRefName: `refs/heads/${branchName}`,
        targetRefName: 'refs/heads/main',
        title: `[${clientCode}] Configuration Capture - ${new Date().toLocaleDateString()}`,
        description: `## Approval Details
**Approved by:** ${approvalData.approvedBy}  
**Approved at:** ${new Date(approvalData.approvedAt).toLocaleString('en-GB')}

## Approval Comments
${approvalData.comments}

## Changes Summary
- 📝 Modified: ${approvalData.summary?.modified || 0}
- ➕ Added: ${approvalData.summary?.added || 0}
- 🗑️ Deleted: ${approvalData.summary?.deleted || 0}

---
*This PR was created via the Capture Review Workflow*
`,
        reviewers: [],
        labels: [
          {
            name: "configuration-capture"
          },
          {
            name: clientCode
          }
        ]
      },
      {
        headers,
        params: { 'api-version': '7.0' }
      }
    );

    const pullRequestId = prResponse.data.pullRequestId;
    const prUrl = `https://dev.azure.com/cyaniccloud/1b238677-4a8d-4243-827e-88a0558c085a/_git/M365-Client-Baseline-Configuration-v3/pullrequest/${pullRequestId}`;

    return {
      success: true,
      pullRequestId,
      prUrl,
      branchName,
      message: 'Pull request created successfully',
      status: 'pending',
      createdBy: approvalData.approvedBy
    };

  } catch (error) {
    console.error('Error creating pull request:', error);

    // Better error message
    if (error.response?.data?.message) {
      throw new Error(`Failed to create PR: ${error.response.data.message}`);
    }
    throw error;
  }
};

const runCapturePipeline = async (
  accessToken,
  projectId,
  pipelineId,
  clientCode,
  excludedProperties = [],
  excludedPaths = []
) => {
  const headers = {
    'Authorization': `Bearer ${accessToken}`,
    'Content-Type': 'application/json'
  };

  const apiUrl = `https://dev.azure.com/cyaniccloud/${projectId}/_apis/pipelines/${pipelineId}/runs?api-version=7.1-preview.1`;

  const body = {
    "stagesToSkip": [],
    "resources": {
      "repositories": {
        "self": {
          "refName": "refs/heads/main"
        }
      }
    },
    "templateParameters": {
      "ClientCode": clientCode
    },
    "variables": {}
  };

  // Add excludedProperties if provided
  if (excludedProperties && excludedProperties.length > 0) {
    body.templateParameters.ExcludedProperties = JSON.stringify(excludedProperties);
  }

  // Add excludedPaths if provided
  if (excludedPaths && excludedPaths.length > 0) {
    body.templateParameters.ExcludedPaths = JSON.stringify(excludedPaths);
  }

  try {
    const response = await axiosInstance.post(apiUrl, body, { headers });
    return response.data.id;
  } catch (error) {
    console.error('Error running capture pipeline:', error);
    throw error;
  }
};

/**
 * Save matching configuration to Azure DevOps
 * Stores confirmed policy matches to reduce false positives in baseline comparison
 */
const saveMatchingConfig = async (accessToken, repoId, clientCode, matchingConfig) => {
  setAuthHeader(accessToken);

  const projectId = '1b238677-4a8d-4243-827e-88a0558c085a';
  const configPath = `/Clients/${clientCode}/.matching-config.json`;

  try {
    //  STEP 1: Get the current commit ID of the main branch
    const refsUrl = `https://dev.azure.com/cyaniccloud/${projectId}/_apis/git/repositories/${repoId}/refs?filter=heads/main&api-version=7.0`;
    const refsResponse = await axiosInstance.get(refsUrl);

    if (!refsResponse.data.value || refsResponse.data.value.length === 0) {
      throw new Error('Could not find main branch');
    }

    const currentCommitId = refsResponse.data.value[0].objectId;
    console.log(`📍 Current main branch commit: ${currentCommitId}`);

    //  STEP 2: Check if file already exists
    let existingObjectId = null;
    try {
      const existingFileUrl = `https://dev.azure.com/cyaniccloud/${projectId}/_apis/git/repositories/${repoId}/items?path=${encodeURIComponent(configPath)}&api-version=7.0`;
      const existingResponse = await axiosInstance.get(existingFileUrl);
      existingObjectId = existingResponse.data.objectId;
      console.log('📄 Existing file found, will update');
    } catch (error) {
      console.log('📄 No existing matching config found, will create new');
    }

    //  STEP 3: Prepare the config content
    const configContent = JSON.stringify(matchingConfig, null, 2);
    const encodedContent = btoa(unescape(encodeURIComponent(configContent)));

    //  STEP 4: Create the commit with correct oldObjectId
    const commitUrl = `https://dev.azure.com/cyaniccloud/${projectId}/_apis/git/repositories/${repoId}/pushes?api-version=7.0`;

    const pushPayload = {
      refUpdates: [
        {
          name: 'refs/heads/main',
          oldObjectId: currentCommitId  //  Use actual current commit, not zeros
        }
      ],
      commits: [
        {
          comment: `Update matching configuration for ${clientCode}`,
          changes: [
            {
              changeType: existingObjectId ? 'edit' : 'add',
              item: {
                path: configPath
              },
              newContent: {
                content: encodedContent,
                contentType: 'base64encoded'
              }
            }
          ]
        }
      ]
    };

    const response = await axiosInstance.post(commitUrl, pushPayload);

    console.log(' Matching configuration saved to Azure DevOps');
    return {
      success: true,
      commitId: response.data.commits[0].commitId
    };

  } catch (error) {
    console.error('Error saving matching config:', error);
    throw error;
  }
};

/**
 * Load matching configuration from Azure DevOps
 * Returns previously confirmed policy matches
 */
const loadMatchingConfig = async (accessToken, repoId, clientCode) => {
  setAuthHeader(accessToken);

  const projectId = '1b238677-4a8d-4243-827e-88a0558c085a';
  const configPath = `/Clients/${clientCode}/.matching-config.json`;

  try {
    //  FIX: Include includeContent=true parameter
    const fileUrl = `https://dev.azure.com/cyaniccloud/${projectId}/_apis/git/repositories/${repoId}/items?path=${encodeURIComponent(configPath)}&includeContent=true&api-version=7.0`;
    const response = await axiosInstance.get(fileUrl);

    console.log('📦 Raw response data:', response.data);

    //  Handle different response formats
    let decodedContent;

    // Case 1: Content is already a parsed object (sometimes Azure DevOps does this)
    if (typeof response.data === 'object' && response.data.version) {
      console.log(' Content is already parsed JSON');
      return response.data;
    }

    // Case 2: Content is in response.data.content as base64
    if (response.data.content) {
      console.log(' Decoding from response.data.content');
      decodedContent = decodeBase64UTF8(response.data.content);
    }
    // Case 3: Content is directly in response.data as string
    else if (typeof response.data === 'string') {
      console.log(' Content is direct string, attempting to decode');
      decodedContent = decodeBase64UTF8(response.data);
    }
    else {
      throw new Error('Unable to find content in response');
    }

    const config = JSON.parse(decodedContent);

    console.log(` Loaded ${config.matches?.length || 0} confirmed matches for ${clientCode}`);
    return config;

  } catch (error) {
    if (error.response?.status === 404) {
      // No config file exists yet
      console.log('No matching configuration found for', clientCode);
      return {
        version: '1.0',
        clientCode: clientCode,
        timestamp: new Date().toISOString(),
        matches: []
      };
    }
    console.error('Error loading matching config:', error);
    throw error;
  }
};

/**
 * Apply matching configuration to comparison results
 * Removes policies from "missing" list if they have confirmed matches
 */
const applyMatchingConfig = (comparisonResults, matchingConfig) => {
  if (!matchingConfig || !matchingConfig.matches || matchingConfig.matches.length === 0) {
    return comparisonResults;
  }

  console.log(`🎯 Applying ${matchingConfig.matches.length} confirmed matches`);

  // Create a Set of baseline displayNames that have been matched
  const matchedBaselineNames = new Set(
    matchingConfig.matches.map(m => m.baselineName)
  );

  console.log('📋 Matched baseline names:', Array.from(matchedBaselineNames));

  // Filter out deviations that have confirmed matches
  const filteredDeviations = comparisonResults.deviations.filter(deviation => {
    // Only check missing policies
    if (deviation.isMissing || deviation.changeType === 'missing') {

      //  Use displayName from the actual policy JSON, not the filename
      const baselineDisplayName = deviation.baselinePolicy?.displayName;

      console.log(` Checking: "${deviation.policyName}" (displayName: "${baselineDisplayName}")`);

      if (baselineDisplayName && matchedBaselineNames.has(baselineDisplayName)) {
        const match = matchingConfig.matches.find(m => m.baselineName === baselineDisplayName);
        console.log(`   MATCH FOUND! Removing "${deviation.policyName}" (matched to "${match.clientName}" with ${match.score}%)`);
        return false; // Remove this deviation
      }
    }

    return true; // Keep this deviation
  });

  // Recalculate counts
  const criticalCount = filteredDeviations.filter(d =>
    d.severity === 'critical' || d.highestSeverity === 'critical'
  ).length;
  const highCount = filteredDeviations.filter(d =>
    d.severity === 'high' || d.highestSeverity === 'high'
  ).length;
  const mediumCount = filteredDeviations.filter(d =>
    d.severity === 'medium' || d.highestSeverity === 'medium'
  ).length;
  const lowCount = filteredDeviations.filter(d =>
    d.severity === 'low' || d.highestSeverity === 'low'
  ).length;
  const prerequisiteCount = filteredDeviations.filter(d =>
    d.severity === 'prerequisite' || d.highestSeverity === 'prerequisite'
  ).length;

  const removedCount = comparisonResults.deviations.length - filteredDeviations.length;
  const newCompliantCount = comparisonResults.compliantCount + removedCount;

  console.log(` Removed ${removedCount} matched policies from deviations`);

  return {
    ...comparisonResults,
    deviations: filteredDeviations,
    compliantCount: newCompliantCount,
    deviationCount: filteredDeviations.length,
    criticalCount,
    highCount,
    mediumCount,
    lowCount,
    prerequisiteCount,
    matchingApplied: true,
    matchesAppliedCount: removedCount
  };
};

/**
 * Delete a specific match from the configuration
 */
const deleteMatch = async (accessToken, repoId, clientCode, baselineName) => {
  setAuthHeader(accessToken);

  try {
    // Load current config
    const config = await loadMatchingConfig(accessToken, repoId, clientCode);

    // Filter out the specific match
    config.matches = config.matches.filter(m => m.baselineName !== baselineName);
    config.timestamp = new Date().toISOString();

    // Save updated config
    await saveMatchingConfig(accessToken, repoId, clientCode, config);

    console.log(` Deleted match for "${baselineName}"`);
    return config;

  } catch (error) {
    console.error('Error deleting match:', error);
    throw error;
  }
};


/**
 * Save accepted deviations configuration to Azure DevOps
 */
const saveAcceptedDeviationsConfig = async (accessToken, repoId, clientCode, config) => {
  setAuthHeader(accessToken);

  const projectId = '1b238677-4a8d-4243-827e-88a0558c085a';
  const configPath = `/Clients/${clientCode}/.accepted-deviations.json`;

  try {
    console.log(' Saving accepted deviations config:', config);

    //  STEP 1: Get the current commit ID of the main branch
    const refsUrl = `https://dev.azure.com/cyaniccloud/${projectId}/_apis/git/repositories/${repoId}/refs?filter=heads/main&api-version=7.0`;
    const refsResponse = await axiosInstance.get(refsUrl);

    if (!refsResponse.data.value || refsResponse.data.value.length === 0) {
      throw new Error('Could not find main branch');
    }

    const currentCommitId = refsResponse.data.value[0].objectId;
    console.log(` Current main branch commit: ${currentCommitId}`);

    //  STEP 2: Check if file already exists
    let existingObjectId = null;
    try {
      const existingFileUrl = `https://dev.azure.com/cyaniccloud/${projectId}/_apis/git/repositories/${repoId}/items?path=${encodeURIComponent(configPath)}&api-version=7.0`;
      const existingResponse = await axiosInstance.get(existingFileUrl);
      existingObjectId = existingResponse.data.objectId;
      console.log('📄 Existing file found, will update');
    } catch (error) {
      console.log('📄 No existing config found, will create new');
    }

    //  STEP 3: Prepare the config content
    const configContent = JSON.stringify(config, null, 2);
    const encodedContent = btoa(unescape(encodeURIComponent(configContent)));

    //  STEP 4: Create the commit with correct oldObjectId
    const commitUrl = `https://dev.azure.com/cyaniccloud/${projectId}/_apis/git/repositories/${repoId}/pushes?api-version=7.0`;

    const pushPayload = {
      refUpdates: [
        {
          name: 'refs/heads/main',
          oldObjectId: currentCommitId  //  Use actual current commit, not zeros
        }
      ],
      commits: [
        {
          comment: `Update accepted deviations configuration for ${clientCode}`,
          changes: [
            {
              changeType: existingObjectId ? 'edit' : 'add',
              item: {
                path: configPath
              },
              newContent: {
                content: encodedContent,
                contentType: 'base64encoded'
              }
            }
          ]
        }
      ]
    };

    const response = await axiosInstance.post(commitUrl, pushPayload);

    console.log(' Accepted deviations configuration saved to Azure DevOps');
    return {
      success: true,
      commitId: response.data.commits[0].commitId
    };

  } catch (error) {
    console.error(' Error saving accepted deviations config:', error);
    throw error;
  }
};

/**
 * Load accepted deviations configuration from Azure DevOps
 */
const loadAcceptedDeviationsConfig = async (accessToken, repoId, clientCode) => {
  setAuthHeader(accessToken);

  const projectId = '1b238677-4a8d-4243-827e-88a0558c085a';
  const configPath = `/Clients/${clientCode}/.accepted-deviations.json`;

  try {
    console.log('📂 Loading accepted deviations config...');

    //  Include includeContent=true parameter (same as loadMatchingConfig)
    const fileUrl = `https://dev.azure.com/cyaniccloud/${projectId}/_apis/git/repositories/${repoId}/items?path=${encodeURIComponent(configPath)}&includeContent=true&api-version=7.0`;
    const response = await axiosInstance.get(fileUrl);

    console.log('📦 Raw response data:', response.data);

    //  Handle different response formats (same as loadMatchingConfig)
    let decodedContent;

    // Case 1: Content is already a parsed object
    if (typeof response.data === 'object' && response.data.version) {
      console.log(' Content is already parsed JSON');
      return response.data;
    }

    // Case 2: Content is in response.data.content as base64
    if (response.data.content) {
      console.log(' Decoding from response.data.content');
      decodedContent = decodeBase64UTF8(response.data.content);
    }
    // Case 3: Content is directly in response.data as string
    else if (typeof response.data === 'string') {
      console.log(' Content is direct string, attempting to decode');
      decodedContent = decodeBase64UTF8(response.data);
    }
    else {
      throw new Error('Unable to find content in response');
    }

    const config = JSON.parse(decodedContent);

    console.log(` Loaded ${config.acceptedDeviations?.length || 0} accepted deviations for ${clientCode}`);
    return config;

  } catch (error) {
    if (error.response?.status === 404) {
      // No config file exists yet
      console.log('ℹ️ No accepted deviations config found for', clientCode);
      return {
        version: '1.0',
        clientCode: clientCode,
        timestamp: new Date().toISOString(),
        acceptedDeviations: []
      };
    }
    console.error(' Error loading accepted deviations config:', error);
    throw error;
  }
};

/**
 * Apply accepted deviations configuration to comparison results
 * Filters out accepted deviations from the deviation list
 */
const applyAcceptedDeviationsConfig = (comparisonResults, acceptedConfig) => {
  if (!acceptedConfig || !acceptedConfig.acceptedDeviations || acceptedConfig.acceptedDeviations.length === 0) {
    return comparisonResults;
  }

  console.log(`🎯 Applying ${acceptedConfig.acceptedDeviations.length} accepted deviations`);

  // Create a lookup structure for fast matching
  // Key: path, Value: Set of accepted property paths
  const acceptedLookup = new Map();

  acceptedConfig.acceptedDeviations.forEach(accepted => {
    if (!acceptedLookup.has(accepted.path)) {
      acceptedLookup.set(accepted.path, new Set());
    }
    accepted.properties.forEach(prop => {
      acceptedLookup.get(accepted.path).add(prop);
    });
  });

  console.log('📋 Accepted deviations by path:', Array.from(acceptedLookup.keys()));

  // Filter deviations
  const filteredDeviations = comparisonResults.deviations.map(deviation => {
    // If this path has no accepted deviations, keep it as-is
    if (!acceptedLookup.has(deviation.path)) {
      return deviation;
    }

    const acceptedProps = acceptedLookup.get(deviation.path);

    // Filter out accepted property deviations
    const remainingDeviations = deviation.deviations.filter(dev => {
      const propertyPath = dev.property || dev.path;
      const isAccepted = acceptedProps.has(propertyPath);

      if (isAccepted) {
        console.log(`   Filtering out accepted deviation: ${propertyPath} in ${deviation.path}`);
      }

      return !isAccepted;
    });

    // If all deviations were accepted, mark the entire policy as compliant
    if (remainingDeviations.length === 0) {
      console.log(`   All deviations accepted for: ${deviation.policyName}`);
      return null; // Will be filtered out
    }

    // Return deviation with filtered list
    return {
      ...deviation,
      deviations: remainingDeviations,
      deviationCount: remainingDeviations.length,
      highestSeverity: determineHighestSeverity(remainingDeviations)
    };
  }).filter(d => d !== null); // Remove nulls (fully accepted policies)

  // Recalculate counts
  const criticalCount = filteredDeviations.filter(d =>
    d.severity === 'critical' || d.highestSeverity === 'critical'
  ).length;
  const highCount = filteredDeviations.filter(d =>
    d.severity === 'high' || d.highestSeverity === 'high'
  ).length;
  const mediumCount = filteredDeviations.filter(d =>
    d.severity === 'medium' || d.highestSeverity === 'medium'
  ).length;
  const lowCount = filteredDeviations.filter(d =>
    d.severity === 'low' || d.highestSeverity === 'low'
  ).length;
  const prerequisiteCount = filteredDeviations.filter(d =>
    d.severity === 'prerequisite' || d.highestSeverity === 'prerequisite'
  ).length;

  const removedCount = comparisonResults.deviations.length - filteredDeviations.length;
  const newCompliantCount = comparisonResults.compliantCount + removedCount;

  console.log(` Filtered ${removedCount} policies with all deviations accepted`);

  return {
    ...comparisonResults,
    deviations: filteredDeviations,
    compliantCount: newCompliantCount,
    deviationCount: filteredDeviations.length,
    criticalCount,
    highCount,
    mediumCount,
    lowCount,
    prerequisiteCount,
    acceptedDeviationsApplied: true,
    acceptedDeviationsCount: removedCount
  };
};

/**
 * Helper function to determine highest severity from a list of deviations
 */
const determineHighestSeverity = (deviations) => {
  const severityOrder = ['critical', 'high', 'medium', 'low', 'prerequisite'];

  for (const severity of severityOrder) {
    if (deviations.some(d => d.severity === severity)) {
      return severity;
    }
  }

  return 'low';
};

/**
 * Delete accepted deviations for a specific policy path
 */
const deleteAcceptedDeviation = async (accessToken, repoId, clientCode, path) => {
  setAuthHeader(accessToken);

  try {
    // Load current config
    const config = await loadAcceptedDeviationsConfig(accessToken, repoId, clientCode);

    // Filter out the specific path
    config.acceptedDeviations = config.acceptedDeviations.filter(a => a.path !== path);
    config.timestamp = new Date().toISOString();

    // Save updated config
    await saveAcceptedDeviationsConfig(accessToken, repoId, clientCode, config);

    console.log(` Deleted accepted deviations for "${path}"`);
    return config;

  } catch (error) {
    console.error('Error deleting accepted deviation:', error);
    throw error;
  }
};

/**
 * Add or update accepted deviations for a policy
 */
const addAcceptedDeviations = async (accessToken, repoId, clientCode, deviationData) => {
  setAuthHeader(accessToken);

  try {
    // Load current config
    const config = await loadAcceptedDeviationsConfig(accessToken, repoId, clientCode);

    if (!config.acceptedDeviations) {
      config.acceptedDeviations = [];
    }

    // Find existing entry for this path
    const existingIndex = config.acceptedDeviations.findIndex(a => a.path === deviationData.path);

    const acceptedEntry = {
      path: deviationData.path,
      policyName: deviationData.policyName,
      category: deviationData.category,
      properties: deviationData.selectedDeviations.map(dev => dev.property || dev.path),
      timestamp: new Date().toISOString(),
      acceptedBy: 'user' // Could be enhanced with actual user info
    };

    if (existingIndex >= 0) {
      // Update existing entry
      console.log(`📝 Updating existing accepted deviations for: ${deviationData.path}`);
      config.acceptedDeviations[existingIndex] = acceptedEntry;
    } else {
      // Add new entry
      console.log(`➕ Adding new accepted deviations for: ${deviationData.path}`);
      config.acceptedDeviations.push(acceptedEntry);
    }

    config.timestamp = new Date().toISOString();

    // Save updated config
    await saveAcceptedDeviationsConfig(accessToken, repoId, clientCode, config);

    console.log(` Accepted ${deviationData.selectedDeviations.length} deviations for "${deviationData.policyName}"`);
    return config;

  } catch (error) {
    console.error('Error adding accepted deviations:', error);
    throw error;
  }
};

const getRemediationPipelineId = async (accessToken, projectId) => {
  setAuthHeader(accessToken);

  try {
    const response = await axiosInstance.get(
      `https://dev.azure.com/cyaniccloud/${projectId}/_apis/pipelines?api-version=7.1-preview.1`
    );

    // Look for the remediation pipeline by exact name
    const remediationPipeline = response.data.value.find(p =>
      p.name === 'Apply-Selective-Remediation'
    );

    if (!remediationPipeline) {
      throw new Error('Remediation pipeline "Apply-Selective-Remediation" not found in Azure DevOps. Please verify the pipeline exists in /Remediation/ folder.');
    }

    console.log(`[REMEDIATION] Found pipeline: ${remediationPipeline.name} (ID: ${remediationPipeline.id})`);

    return remediationPipeline.id;
  } catch (error) {
    console.error('[REMEDIATION] Error finding pipeline:', error);
    throw error;
  }
};

/**
 * Creates a policy remediation request
 * 1. Creates remediation branch
 * 2. Adds remediation manifest
 * 3. Triggers selective remediation pipeline
 */
const createPolicyRemediation = async (
  accessToken,
  repoId,
  clientCode,
  remediationData
) => {
  setAuthHeader(accessToken);

  const projectId = '1b238677-4a8d-4243-827e-88a0558c085a';
  const timestamp = Date.now();
  const branchName = `client-${clientCode}-remediation-${timestamp}`;

  try {
    console.log('[REMEDIATION] Creating policy remediation...');

    //  STEP 1: Get current main branch commit (following saveMatchingConfig pattern)
    const refsUrl = `https://dev.azure.com/cyaniccloud/${projectId}/_apis/git/repositories/${repoId}/refs?filter=heads/main&api-version=7.0`;
    const refsResponse = await axiosInstance.get(refsUrl);

    if (!refsResponse.data.value || refsResponse.data.value.length === 0) {
      throw new Error('Could not find main branch');
    }

    const mainCommitId = refsResponse.data.value[0].objectId;
    console.log(`[REMEDIATION] Current main branch commit: ${mainCommitId}`);

    //  STEP 2: Create remediation branch from current main commit
    console.log(`[REMEDIATION] Creating branch: ${branchName}`);
    await axiosInstance.post(
      `https://dev.azure.com/cyaniccloud/${projectId}/_apis/git/repositories/${repoId}/refs?api-version=7.0`,
      [{
        name: `refs/heads/${branchName}`,
        oldObjectId: '0000000000000000000000000000000000000000',  //  This is correct for NEW branch creation
        newObjectId: mainCommitId  //  Point new branch to current main
      }]
    );

    console.log('[REMEDIATION] Branch created successfully');

    //  STEP 3: Create remediation manifest
    const manifest = {
      version: '1.0',
      clientCode: clientCode,
      timestamp: new Date().toISOString(),
      requestedBy: remediationData.requestedBy,
      reason: remediationData.reason || 'Policy remediation from baseline comparison',
      policies: Array.isArray(remediationData.policies)
        ? remediationData.policies
        : [remediationData]  // Support single policy or array
    };

    const manifestContent = JSON.stringify(manifest, null, 2);
    const manifestEncoded = btoa(unescape(encodeURIComponent(manifestContent)));

    //  STEP 4: Commit manifest to NEW remediation branch
    console.log('[REMEDIATION] Committing remediation manifest to branch...');
    await axiosInstance.post(
      `https://dev.azure.com/cyaniccloud/${projectId}/_apis/git/repositories/${repoId}/pushes?api-version=7.0`,
      {
        refUpdates: [{
          name: `refs/heads/${branchName}`,
          oldObjectId: mainCommitId  //  Use main's commit as base for new branch commit
        }],
        commits: [{
          comment: `Policy Remediation Request\n\nRequested by: ${remediationData.requestedBy}\nPolicies: ${manifest.policies.length}\nTimestamp: ${manifest.timestamp}`,
          changes: [{
            changeType: 'add',
            item: { path: `/Clients/${clientCode}/.remediation-manifest.json` },
            newContent: {
              content: manifestEncoded,
              contentType: 'base64encoded'
            }
          }]
        }]
      }
    );

    console.log('[REMEDIATION] Manifest committed successfully');

    //  STEP 5: Get the remediation pipeline ID
    console.log('[REMEDIATION] Finding remediation pipeline...');
    const pipelineId = await getRemediationPipelineId(accessToken, projectId);

    //  STEP 6: Trigger remediation pipeline
    console.log('[REMEDIATION] Triggering pipeline...');
    const buildResponse = await axiosInstance.post(
      `https://dev.azure.com/cyaniccloud/${projectId}/_apis/pipelines/${pipelineId}/runs?api-version=7.1-preview.1`,
      {
        stagesToSkip: [],
        resources: {
          repositories: {
            self: {
              refName: `refs/heads/${branchName}`
            }
          }
        },
        templateParameters: {
          ClientCode: clientCode
        }
      }
    );

    console.log('[REMEDIATION] Pipeline triggered successfully');

    return {
      success: true,
      branchName,
      buildId: buildResponse.data.id,
      pipelineUrl: `https://dev.azure.com/cyaniccloud/${projectId}/_build/results?buildId=${buildResponse.data.id}`,
      message: 'Remediation pipeline started successfully',
      policiesCount: manifest.policies.length
    };

  } catch (error) {
    console.error('[REMEDIATION] Error creating policy remediation:', error);

    if (error.response) {
      console.error('[REMEDIATION] Response status:', error.response.status);
      console.error('[REMEDIATION] Response data:', error.response.data);
    }

    throw error;
  }
};


/**
 * Call AI API to get policy explanations
 * @param {string} policyJson - The policy JSON content to explain
 * @param {object} options - Configuration options
 * @returns {Promise<string>} - AI-generated explanation
 */
const callAiApi_old = async (policyJson, options = {}) => {
  const {
    apiUrl = process.env.VUE_APP_AI_API_URL,
    apiKey = process.env.VUE_APP_AI_API_KEY,
    model = 'glm-5-2-nvfp4',
    maxTokens = 1048576,
    stream = true
  } = options;

  if (!apiKey) {
    throw new Error('AI API key not configured. Please set VUE_APP_AI_API_KEY in your environment.');
  }

  const headers = {
    'Accept': 'application/json, text/plain, */*',
    'Authorization': `Bearer ${apiKey}`,
    'Content-Type': 'application/json'
  };

  const prompt = `Please help users understand what this policy is doing. Explain it in a way that most users would understand. Here's the policy JSON:\n\n${policyJson}`;

  const payload = {
    model: model,
    max_tokens: maxTokens,
    messages: [
      { role: 'user', content: prompt }
    ]
  };

  try {
    if (stream) {
      // Streaming response handling
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: headers,
        body: JSON.stringify({ ...payload, stream: true })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(`AI API error: ${response.status} - ${errorData.error?.message || 'Unknown error'}`);
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let collectedText = '';

      // eslint-disable-next-line no-constant-condition
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        const chunk = decoder.decode(value);
        const lines = chunk.split('\n').filter(line => line.trim() !== '');

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            const data = line.slice(6).trim();
            if (data === '[DONE]') break;

            try {
              const parsed = JSON.parse(data);
              const content = parsed.choices?.[0]?.delta?.content;
              if (content) {
                collectedText += content;
              }
            } catch (e) {
              // Skip malformed JSON chunks
              console.warn('Skipping malformed JSON chunk:', e);
            }
          }
        }
      }

      return collectedText;
    } else {
      // Non-streaming response
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: headers,
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(`AI API error: ${response.status} - ${errorData.error?.message || 'Unknown error'}`);
      }

      const data = await response.json();
      return data.choices?.[0]?.message?.content || '';
    }
  } catch (error) {
    console.error('Error calling AI API:', error);
    throw error;
  }
};


const callAiApi = async (policyJson, options = {}, onChunk = null) => {
  const {
    apiUrl = process.env.VUE_APP_AI_API_URL,
    apiKey = process.env.VUE_APP_AI_API_KEY,
    model = 'glm-5-2-nvfp4',
    maxTokens = 1048576,
    stream = false
  } = options;

  if (!apiKey) {
    throw new Error('AI API key not configured. Please set VUE_APP_AI_API_KEY in your environment.');
  }

  const headers = {
    'Accept': 'application/json, text/plain, */*',
    'Authorization': `Bearer ${apiKey}`,
    'Content-Type': 'application/json'
  };

  const prompt = `You are a technical documentation expert. Explain this policy configuration in a clear, professional manner that business users can understand.

FORMAT YOUR RESPONSE AS CLEAN HTML:
- Start with an <h2> for the main title
- Use <h3> for major sections (like "What This Does", "Key Settings", "Why This Matters")
- Use <h4> for subsections within each major section
- Use <p> for explanatory text
- Use <ul> and <li> for feature lists and bullet points
- Use <code> for technical values, IDs, or specific settings
- Use <strong> to emphasize important concepts
- Keep paragraphs concise (2-4 sentences max)

STRUCTURE:
1. Start with a clear H2 title
2. Opening paragraph explaining the purpose
3. "What This Does" section (H3)
4. "Key Settings" or "Configuration Details" section (H3)
5. "Why This Matters" section (H3) - explain business impact

Do NOT include <html>, <body>, <head>, or wrapper <div> tags.
Keep the language clear and professional, avoiding unnecessary jargon.

Here's the policy JSON to explain:
${policyJson}`;

  const payload = {
    model: model,
    max_tokens: maxTokens,
    stream: stream,
    messages: [
      { role: 'user', content: prompt }
    ]
  };

  try {
    const response = await fetch(apiUrl, {
      method: 'POST',
      headers: headers,
      body: JSON.stringify(payload)
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(`AI API error: ${response.status} - ${errorData.error?.message || 'Unknown error'}`);
    }

    if (stream && onChunk) {
      // Streaming response with real-time callback
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let collectedText = '';
      let buffer = '';

      // eslint-disable-next-line no-constant-condition
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');

        // Keep the last potentially incomplete line in the buffer
        buffer = lines.pop() || '';

        for (const line of lines) {
          const trimmed = line.trim();
          if (!trimmed || !trimmed.startsWith('data: ')) continue;

          const data = trimmed.slice(6);
          if (data === '[DONE]') continue;

          try {
            const parsed = JSON.parse(data);
            const content = parsed.choices?.[0]?.delta?.content;
            if (content) {
              collectedText += content;
              onChunk(collectedText); // Call with accumulated text
            }
          } catch (e) {
            // Skip malformed chunks
          }
        }
      }

      // Process any remaining buffer
      if (buffer.trim().startsWith('data: ')) {
        const data = buffer.trim().slice(6);
        if (data !== '[DONE]') {
          try {
            const parsed = JSON.parse(data);
            const content = parsed.choices?.[0]?.delta?.content;
            if (content) {
              collectedText += content;
              onChunk(collectedText);
            }
          } catch (e) {
            // Skip malformed chunks
          }
        }
      }

      return collectedText;

    } else {
      // Non-streaming response
      const data = await response.json();
      return data.choices?.[0]?.message?.content || '';
    }

  } catch (error) {
    console.error('Error calling AI API:', error);
    throw error;
  }
};

const runJITPipeline = async (
  accessToken,
  projectId,
  pipelineId,
  parameters
) => {
  setAuthHeader(accessToken);
  const apiUrl = `https://dev.azure.com/cyaniccloud/${projectId}/_apis/pipelines/${pipelineId}/runs?api-version=7.1-preview.1`;

  const body = {
    stagesToSkip: [],
    resources: {
      repositories: {
        self: {
          refName: "refs/heads/main"
        }
      }
    },
    templateParameters: {
      ClientCode: parameters.ClientCode,
      JiraTicket: parameters.JiraTicket,
      Role: parameters.Role,
      DurationHours: parameters.DurationHours,
      Justification: parameters.Justification,
      Requestor: parameters.Requestor
    },
    variables: {}
  };

  try {
    const response = await axiosInstance.post(apiUrl, body);
    return response.data.id;
  } catch (error) {
    console.error('Error running JIT pipeline:', error);
    throw error;
  }
};

const fetchJITAuditLogs = async (accessToken, repoId, clientCode = null) => {
  setAuthHeader(accessToken);

  try {
    const basePath = clientCode
      ? `/AuditLogs/JIT-Admin/${clientCode}`
      : '/AuditLogs/JIT-Admin';

    console.log(`Fetching JIT audit logs from: ${basePath}`);

    // First, fetch directory contents to get list of files
    const listUrl = `https://dev.azure.com/cyaniccloud/_apis/git/repositories/${repoId}/items?scopePath=${basePath}&recursionLevel=Full&api-version=7.1`;

    const response = await axiosInstance.get(listUrl);
    const items = response.data.value || [];

    // Filter for JSON files only
    const jsonFiles = items.filter(
      item => item.gitObjectType === 'blob' && item.path.endsWith('.json')
    );

    console.log(`Found ${jsonFiles.length} audit log files`);

    // Now fetch the ACTUAL CONTENT of each file
    const auditLogs = [];
    for (const file of jsonFiles) {
      try {
        console.log(`Fetching content for: ${file.path}`);

        // Use the includeContent parameter to get the actual file content
        const contentUrl = `https://dev.azure.com/cyaniccloud/_apis/git/repositories/${repoId}/items?path=${encodeURIComponent(file.path)}&includeContent=true&api-version=7.1`;
        const contentResponse = await axiosInstance.get(contentUrl);

        console.log('Content response structure:', Object.keys(contentResponse.data));

        // The content is base64 encoded in the 'content' property
        if (contentResponse.data.content) {
          console.log('Content type:', typeof contentResponse.data.content);
          console.log('Content length:', contentResponse.data.content.length);
          console.log('First 100 chars:', contentResponse.data.content.substring(0, 100));

          // Try to decode - Azure DevOps returns base64 with newlines that need to be removed
          let decoded;
          try {
            // Remove any whitespace/newlines from base64 string
            const cleanBase64 = contentResponse.data.content.replace(/\s/g, '');
            decoded = atob(cleanBase64);
          } catch (atobError) {
            console.log('atob failed, trying decodeBase64UTF8...');
            // Fall back to your existing decode function
            decoded = decodeBase64UTF8(contentResponse.data.content);
          }

          const logData = JSON.parse(decoded);

          console.log('Successfully parsed audit log:', logData.assignmentId);
          auditLogs.push(logData);
        } else {
          console.warn(`No content property found. Available properties:`, Object.keys(contentResponse.data));
        }
      } catch (error) {
        console.error(`Failed to fetch/parse audit log: ${file.path}`, error);
        console.error('Error details:', error.message);
      }
    }

    // Sort by timestamp, newest first
    auditLogs.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));

    console.log(`Successfully loaded ${auditLogs.length} audit logs with content`);
    return auditLogs;
  } catch (error) {
    console.error('Error fetching JIT audit logs:', error);
    if (error.response?.status === 404) {
      console.log('Audit log directory not found, returning empty array');
      return [];
    }
    throw error;
  }
};

export {
  formatFileSize,
  calculateBackupSize,
  fetchClientBackups,
  fetchBackupContent,
  restoreFromBackup,
  deleteOldBackups,
  fetchProjects,
  fetchPipelines,
  runPipeline,
  fetchPipelinesbyName,
  triggerBuild,
  fetchJsonFile,
  fetchClients,
  compareJsonObjects,
  fetchAndCompareFiles,
  fetchAndCompareAllFiles,
  fetchAndCompareAllFilesv2,
  fetchAndCompareAllFilesv3,
  fetchAndCompareAllFilesv4,
  fetchAndCompareAllFilesv5,
  fetchAndCompareAllFilesv6,
  compareConfigurations,
  fetchAllJsonFiles,
  fetchAllJsonFilesv2,
  fetchAllJsonFilesv3,
  listAzureTables,
  getTableContents,
  createClientTable,
  updateClientTable,
  updateEntity,
  deleteClientTable,
  triggerAppDeployBuild,
  fetchPipelinesWithFilter,
  fetchPipelinesWithFolderFilter,
  fetchPipelinesWithFilterandProject,
  runComponentPipeline,
  runComponentPipelinev2,
  getPipelineStatus,
  triggerOnboardBuild,
  triggerAppDeployBuild_old,
  fetchYamlFile,
  updateYamlFileContent,
  runFullBaselinePipeline,
  runCustomBaselinePipeline,
  getRepositories,
  getFolderStructureAndFiles,
  getAllItemsGroupedByTopLevelArea,
  getAllPipelineDefinitions,
  getPipelineDefinitionInfo,
  getExistingSchedules,
  UpdateClientSchedule,
  updateYamlFileContentSchedule,
  triggerNewOnboardBuild,
  fetchDirectoryContents,
  runOffboardPipeline,
  getClientBranches,
  getClientOverview,
  detectClientDrift,
  compareBaselineVsClientOptimized,
  mergeCaptureToMain,
  getDirectoryTreeOptimized,
  compareFilePairSimple,
  determineSeverity,
  getCategoryFromPath,
  runCapturePipeline,
  decodeBase64UTF8,
  fetchFileContent,
  getClientMergedPRs,
  deleteMatch,
  applyMatchingConfig,
  loadMatchingConfig,
  saveMatchingConfig,
  saveAcceptedDeviationsConfig,
  loadAcceptedDeviationsConfig,
  applyAcceptedDeviationsConfig,
  deleteAcceptedDeviation,
  addAcceptedDeviations,
  determineHighestSeverity,
  createPolicyRemediation,
  getRemediationPipelineId,
  callAiApi_old,
  callAiApi,
  runJITPipeline,
  fetchJITAuditLogs,
};
